/**
 * Sharp's banen-diagram.
 *
 * Eigen render-pad naast React Flow, zoals het BMC-canvas en de glossary —
 * en om dezelfde reden: de plaatsing volgt hier niet uit coördinaten maar uit
 * het model. Een stap staat in de baan van wie 'm uitvoert, en dat is de
 * Assignment; horizontaal staat 'ie op de plek die de stroom 'm geeft.
 *
 * De afleidingen zelf staan in `@ae/domain` (`sharpSwimlane.ts`), zodat de
 * renderer en de validatie hetzelfde antwoord geven op "in welke baan hoort
 * dit" en "is dit een overdracht". Zie §11a van
 * `archimate-sharp-workflow-extensie.md` voor de invarianten.
 *
 * Slepen naar een baan legt de toewijzing aan, of verhuist 'm — dat is de
 * enige mutatie die dit canvas doet (§11a punt 1). De baan zonder naam is de
 * plek waar werk zonder uitvoerder terechtkomt, en tegelijk de dropzone om
 * 'm daar weer uit te halen.
 */
import {
  alsBeslisstap,
  type Concept,
  type Diagram,
  isBaanBewoner,
  isBaanKoppeling,
  isBeslisstap,
  isOverdracht,
  isStap,
  knikStuurpunt,
  knikUitPunt,
  koppelingVoor,
  leidBanenAf,
  type NodeStyle,
  type Relation,
  type SwimlaneKnik,
  sorteerBanen,
  stroomRegel,
  toewijzingenBijBaanwissel,
  XOR_STEREOTYPE,
} from "@ae/domain";
import {
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  type Node,
  Position,
} from "@xyflow/react";
import {
  type CSSProperties,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual, validRelationTypesBetween } from "../../registry";
import { claimObjectLock, releaseObjectLock } from "../../state/objectLocks";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { confirmHardDelete } from "../cascadeConfirm";
import { colorForConcept } from "../layerColors";
import { readDroppedTypeName } from "../palette/dragTypeName";
import { CanvasContextMenu, type CanvasMenuStand } from "./CanvasContextMenu";
import { CanvasToolbar } from "./CanvasToolbar";
import { ConceptContextMenu, type ConceptMenuStand } from "./ConceptContextMenu";
import { hernoemConcept } from "./hernoemConcept";
import { DIAGRAM_BASE_FONT } from "./passendeFont";
import { type RelatieMenuStand, RelationContextMenu } from "./RelationContextMenu";
import { RibbonPortal } from "./RibbonSlot";
import { heeftStandaardnaam } from "./standaardNaam";
import { kolommen } from "./swimlaneLayout";
import { VertakkingKeuze, type VertakkingSoort, type VertakkingVraag } from "./VertakkingKeuze";
import { ZoomPan, type ZoomPanActies } from "./ZoomPan";

const KAART_BREEDTE = 190;
/** Een vertakking is een punt op de lijn, geen vlak: Sharp tekent 'm klein. */
const VERTAKKING_MAAT = 30;
/** Hoe dicht een kaartmidden bij een buurmidden moet komen om eraan vast te klikken. */
const MAGNEET = 8;
/**
 * Het ruimere bereik voor een vertakking of samenvoeging. Die ligt per
 * definitie óp de stroomlijn tussen twee stappen, dus een stip die je er
 * ongeveer neerzet hoort er precies op te belanden.
 */
const MAGNEET_PUNT = 40;
/**
 * De inkt van dit canvas.
 *
 * Eén kleur voor élke lijn: de rand van een kaart, de stromen, de symbolen en
 * de baanscheidingen. Sharp's figuren zijn lijntekeningen — daar onderscheidt
 * niets zich door kleur, alles door VORM. Een amberkleurig kader naast een
 * grijze stroom leest als twee soorten tekening op één vel.
 */
const INKT = "#6B7280";
/** De aangewezen lijn of kaart. */
const INKT_GEKOZEN = "#2563EB";
/** Hoe schuin een stroom mag aankomen en toch als "recht" telt. */
const RECHT_MARGE = 4;
/**
 * Waar het symbool van een resultaat of een wachttijd staat binnen z'n kaart.
 *
 * Een stap is een vlak en z'n rand is de aanhechting. Een resultaat en een
 * wachttijd zijn een SYMBOOL in een veel breder vak — de cirkel staat links,
 * de stopwatch in het midden — en een lijn die op de rand van dat vak stopt
 * eindigt in het luchtledige, tientallen punten van het teken waar 'ie naar
 * wijst. Deze getallen komen uit de tekening hieronder: 12 punten padding
 * (px-3) en een symbool van 26 breed.
 */
const SYMBOOL_MARGE = 12;
const SYMBOOL_MAAT = 26;

/**
 * Is dit een vertakking of een samenvoeging?
 *
 * Staat op moduleniveau omdat zowel de berekening van de aanhechtingen als de
 * tekening 'm nodig heeft. Een junction is een PUNT: elke stroom komt op
 * hetzelfde punt uit, en dat punt is het midden. Zou je 'm als vak behandelen
 * — rechterrand eruit, linkerrand erin, inkomende lijnen over de hoogte
 * verdeeld — dan hechten de lijnen aan de hoeken van een vak van 30 bij 30
 * terwijl de stip er maar 14 meet, en raken ze de stip nooit.
 */
const VERTAKKING_STEREOTYPES = [
  "sharp.ExclusiveBranch",
  "sharp.ParallelBranch",
  "sharp.InclusiveBranch",
  "sharp.MergePoint",
];
function isVertakking(c: Concept): boolean {
  if ((c.stereotypes ?? []).some((st) => VERTAKKING_STEREOTYPES.includes(st))) return true;
  return c.typeId === "archimate.AndJunction" || c.typeId === "archimate.OrJunction";
}
const KAART_HOOGTE = 62;
const KOLOM_AFSTAND = 250;
const BAAN_HOOGTE = 118;
const KOP_BREEDTE = 190;
/**
 * Wat een baankop nodig heeft wanneer de naam verticaal staat: twee regels
 * van 13 punten plus wat lucht. Krapper en de tweede regel valt buiten de
 * kop — precies wat er bij 52 gebeurde.
 */
const KOP_BREEDTE_STAAND = 68;
const MARGE = 24;
/** De titelbalk bovenaan, met de naam van het diagram (Sharp Fig 8.1). */
const TITEL_HOOGTE = 34;
/** Waar de eerste baan begint: onder de titel en onder de open strook. */
const RASTER_TOP = MARGE + TITEL_HOOGTE + BAAN_HOOGTE;

type Plek = { x: number; y: number };
type Maat = { breedte: number; hoogte: number };

/**
 * Het juiste `dropEffect` bij wat er gesleept wordt.
 *
 * De browser weigert een drop zwijgend wanneer `dropEffect` buiten
 * `effectAllowed` valt. Het palet sleept met `"copy"`, een stapkaart die van
 * baan wisselt met `"move"` — één vaste waarde klopt dus nooit voor allebei.
 */
function sleepEffect(dt: DataTransfer): "copy" | "move" {
  return dt.effectAllowed === "move" || dt.effectAllowed === "linkMove" ? "move" : "copy";
}

export function SwimlaneCanvas({ diagram }: { diagram: Diagram }) {
  const { t, i18n } = useTranslation();
  const revision = useWorkspaceData((s) => s.revision);
  const selectConcept = useUi((s) => s.selectConcept);
  const selectInstance = useUi((s) => s.selectInstance);
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  const selectedInstanceId = useUi((s) => s.selectedInstanceId);
  const selectRelation = useUi((s) => s.selectRelation);
  const selectedRelationId = useUi((s) => s.selectedRelationId);
  const lang = i18n.language.startsWith("nl") ? "nl" : "en";

  const model = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const concepts = store.listConcepts().filter((c) => !c.tombstoneInBranch);
    const relations = store.listRelations().filter((r) => !r.tombstoneInBranch);
    const perId = new Map(concepts.map((c) => [c.id, c] as const));
    const entries = Object.entries(diagram.viewState ?? {}).filter(([, ve]) => !ve.hidden);
    const opDiagram = new Set(entries.map(([sleutel, ve]) => ve.ref ?? sleutel));

    // De baan zonder uitvoerder verschijnt alleen wanneer er werk in ligt.
    const banen = leidBanenAf(concepts, relations, [...opDiagram]);
    const baanIds = new Set(banen.map((b) => b.id).filter((id): id is string => id !== null));
    const rijen = new Map<string, number>();
    const knikken = new Map<string, SwimlaneKnik>();
    for (const [sleutel, ve] of entries) {
      const ref = ve.ref ?? sleutel;
      if (typeof ve.swimlaneRij === "number") rijen.set(ref, ve.swimlaneRij);
      if (ve.swimlaneKnik) knikken.set(ref, ve.swimlaneKnik);
    }
    const gesorteerdeBanen = sorteerBanen(banen, rijen);
    // Elke baan heeft z'n eigen hoogte: de standaard past één rij kaarten, en
    // wie er twee onder elkaar wil zetten maakt de baan hoger.
    const baanHoogtes = gesorteerdeBanen.map((b) => {
      const eigen = b.id ? diagram.viewState?.[b.id]?.swimlaneBaanHoogte : undefined;
      return typeof eigen === "number" ? Math.max(60, eigen) : BAAN_HOOGTE;
    });
    const baanToppen: number[] = [];
    let loper = RASTER_TOP;
    for (const h of baanHoogtes) {
      baanToppen.push(loper);
      loper += h;
    }
    const onderkantBanen = loper;
    // Staan álle baannamen verticaal, dan heeft de kop die 190 punten niet
    // nodig; dan is 'ie een smalle rand zoals in Sharp's eigen figuren. Eén
    // horizontale naam en de brede kop komt terug — die heeft de ruimte wel
    // nodig.
    const bewoners = gesorteerdeBanen.filter((b) => b.id !== null);
    const alleStaand =
      bewoners.length > 0 &&
      bewoners.every(
        (b) =>
          (b.id ? (diagram.viewState?.[b.id]?.nodeStyle?.labelOrientation ?? null) : null) ===
          "vertical",
      );
    const kopBreedte = alleStaand ? KOP_BREEDTE_STAAND : KOP_BREEDTE;
    /** Van bewoner-id naar rij — de weg terug van `swimlaneBaanId`. */
    const baanIndex = new Map<string, number>();
    gesorteerdeBanen.forEach((b, i) => {
      if (b.id) baanIndex.set(b.id, i);
    });
    const baanVanConcept = new Map<string, number>();
    gesorteerdeBanen.forEach((baan, rij) => {
      for (const id of baan.stapIds) baanVanConcept.set(id, rij);
    });

    /**
     * Elke kaart is een VOORKOMEN, geen concept.
     *
     * Zo werkt elk ander canvas ook: de sleutel van de viewState-entry is het
     * ding op het scherm, `ref` wijst naar wat het voorstelt. Dezelfde stap
     * kan er dus twee keer op staan, elk met een eigen plek. De plek komt uit
     * `position` en wordt niet meer afgeleid — wie 'm versleept bepaalt 'm.
     */
    const kaarten = new Map<
      string,
      {
        sleutel: string;
        concept: Concept;
        /** De baan waar 'ie aan vastzit, als die is vastgelegd. */
        rij?: number;
        x: number;
        y: number;
        breedte: number;
        hoogte: number;
      }
    >();
    let ongeplaatst = 0;
    for (const [sleutel, ve] of entries) {
      if (ve.type === "relation" || ve.type === "supernode") continue;
      const concept = perId.get(ve.ref ?? sleutel);
      // Alles wat geen baan is, is een kaart: stappen, maar net zo goed een
      // procesresultaat, een gebeurtenis of een werkobject. Die vielen eerder
      // tussen wal en schip — geen stap, dus geen kaart; geen uitvoerder, dus
      // geen baan — en waren daardoor onzichtbaar.
      if (!concept || baanIds.has(concept.id)) continue;
      const x = ve.position?.x ?? 0;
      const bewaardeY = ve.position?.y ?? 0;
      if (x === 0 && bewaardeY === 0) ongeplaatst += 1;
      const kaartHoogte = ve.height ?? KAART_HOOGTE;
      /**
       * De kaart hangt aan z'n BAAN, niet aan een y.
       *
       * Staat er een baan vastgelegd, dan bepaalt die waar de kaart hoort en
       * blijft de y binnen die baan — wordt de baan lager of schuift 'ie op,
       * dan schuift de kaart mee. Zonder dat veld (diagrammen van vóór deze
       * koppeling) blijft de y leidend, precies zoals 'ie stond.
       */
      const rijVanId = ve.swimlaneBaanId ? baanIndex.get(ve.swimlaneBaanId) : undefined;
      const y = bewaardeY;
      kaarten.set(sleutel, {
        sleutel,
        concept,
        ...(rijVanId !== undefined ? { rij: rijVanId } : {}),
        x,
        y,
        breedte: ve.width ?? KAART_BREEDTE,
        hoogte: kaartHoogte,
      });
    }

    /**
     * De inhoud van een baan staat verticaal in het MIDDEN van die baan.
     *
     * De opgeslagen y zegt waar een kaart stond toen je 'm neerzette; de baan
     * kan daarna hoger of lager zijn geworden, of opgeschoven doordat een
     * baan erboven veranderde. Dan hoort de inhoud mee te bewegen en
     * gecentreerd te blijven — niet tegen een rand te plakken.
     *
     * Het gaat om de GROEP, niet om elke kaart apart: twee stappen onder
     * elkaar in dezelfde baan houden hun onderlinge afstand en worden samen
     * gecentreerd. Alleen wat aan een baan vastzit doet mee; kaarten zonder
     * die koppeling (diagrammen van vóór `swimlaneBaanId`) blijven staan waar
     * ze staan.
     */
    const perBaanKaarten = new Map<number, string[]>();
    for (const kaart of kaarten.values()) {
      if (kaart.rij === undefined) continue;
      perBaanKaarten.set(kaart.rij, [...(perBaanKaarten.get(kaart.rij) ?? []), kaart.sleutel]);
    }
    for (const [rij, sleutels] of perBaanKaarten) {
      const top = baanToppen[rij] ?? RASTER_TOP;
      const hoog = baanHoogtes[rij] ?? BAAN_HOOGTE;
      const groep = sleutels
        .map((k) => kaarten.get(k))
        .filter((k): k is NonNullable<typeof k> => !!k);
      if (groep.length === 0) continue;
      const blokTop = Math.min(...groep.map((k) => k.y));
      const blokOnder = Math.max(...groep.map((k) => k.y + k.hoogte));
      const verschuiving = Math.round(top + (hoog - (blokOnder - blokTop)) / 2 - blokTop);
      if (verschuiving === 0) continue;
      for (const kaart of groep) {
        kaarten.set(kaart.sleutel, { ...kaart, y: kaart.y + verschuiving });
      }
    }

    // Het eerste voorkomen per stap — waar een stroom aan hangt wanneer de
    // relatie geen voorkomen aanwijst. Zelfde regel als op het gewone canvas.
    const eersteVoorkomen = new Map<string, string>();
    for (const kaart of kaarten.values()) {
      if (!eersteVoorkomen.has(kaart.concept.id)) {
        eersteVoorkomen.set(kaart.concept.id, kaart.sleutel);
      }
    }

    const stappen = new Map(
      [...kaarten.values()]
        .filter((k) => isStap(k.concept))
        .map((k) => [k.concept.id, k.concept] as const),
    );
    const opKaart = new Set([...kaarten.values()].map((k) => k.concept.id));
    const stromen = relations
      .filter(
        (r) =>
          (r.typeId === "archimate.Flow" ||
            r.typeId === "archimate.Triggering" ||
            r.typeId === "archimate.Realization") &&
          opKaart.has(r.sourceConceptId) &&
          opKaart.has(r.targetConceptId),
      )
      .map((r) => ({ relation: r, overdracht: isOverdracht(r, concepts, relations) }));

    /**
     * Waar een stroom aan een kaart vastzit.
     *
     * Alles op hetzelfde punt laten uitkomen geeft een bos lijnen dat over
     * elkaar heen loopt; twee stromen naar dezelfde stap zijn dan niet meer
     * uit elkaar te houden. De aanhechtingen worden daarom over de rand
     * verdeeld — de rechterrand voor wat vertrekt, de linkerrand voor wat
     * aankomt — in de volgorde waarin de overkant hoger of lager ligt, zodat
     * de lijnen elkaar niet kruisen.
     */
    const voorkomenVan = (conceptId: string, aangewezen?: string) =>
      aangewezen && kaarten.has(aangewezen) ? aangewezen : eersteVoorkomen.get(conceptId);
    const inkomend = new Map<string, string[]>();
    for (const { relation } of stromen) {
      const ve = diagram.viewState?.[relation.id];
      const van = voorkomenVan(relation.sourceConceptId, ve?.sourceInstanceId);
      const naar = voorkomenVan(relation.targetConceptId, ve?.targetInstanceId);
      if (!van || !naar) continue;
      void van;
      inkomend.set(naar, [...(inkomend.get(naar) ?? []), relation.id]);
    }
    const overkantY = (relId: string, vanafSleutel: string) => {
      const rel = stromen.find((x) => x.relation.id === relId)?.relation;
      if (!rel) return 0;
      const ve = diagram.viewState?.[relId];
      const van = voorkomenVan(rel.sourceConceptId, ve?.sourceInstanceId);
      const naar = voorkomenVan(rel.targetConceptId, ve?.targetInstanceId);
      const ander = van === vanafSleutel ? naar : van;
      const k = ander ? kaarten.get(ander) : undefined;
      return k ? k.y + k.hoogte / 2 : 0;
    };
    const aanhechting = new Map<string, { uit?: number; in?: number }>();
    const verdeel = (lijst: Map<string, string[]>, kant: "uit" | "in") => {
      for (const [sleutel, ids] of lijst) {
        const kaart = kaarten.get(sleutel);
        if (!kaart) continue;
        // Op een junction komt alles op één punt samen; dat is precies wat 'm
        // een junction maakt. Niets te verdelen dus.
        if (isVertakking(kaart.concept)) continue;
        const midden = kaart.y + kaart.hoogte / 2;
        // Eén lijn valt niets te scheiden: die hoort in het midden, hoe schuin
        // 'ie ook aankomt. De verdeling hieronder bestaat alleen om meerdere
        // lijnen uit elkaar te houden, en trok anders juist de enige lijn naar
        // een procesresultaat omhoog — naast de stip in plaats van erop.
        if (ids.length === 1 && ids[0]) {
          const enige = ids[0];
          aanhechting.set(enige, { ...(aanhechting.get(enige) ?? {}), [kant]: midden });
          continue;
        }
        /**
         * Wat recht binnenkomt houdt het midden; alleen wat schuin aankomt
         * wordt verdeeld.
         *
         * Eerder ging de hele stapel over de rand: bij twee inkomende stromen
         * kwamen ze op een derde en twee derde uit. Dat scheidt ze netjes,
         * maar het knikt óók de lijn die kaarsrecht had kunnen lopen — en
         * juist die hoofdlijn is wat je op een banendiagram leest. Een stap
         * die ernaast of eronder ligt heeft die knik nodig om herkenbaar te
         * blijven; een stap die er pal tegenover staat niet.
         */
        const recht: string[] = [];
        const boven: string[] = [];
        const onder: string[] = [];
        for (const id of ids) {
          const y = overkantY(id, sleutel);
          if (Math.abs(y - midden) <= RECHT_MARGE) recht.push(id);
          else if (y < midden) boven.push(id);
          else onder.push(id);
        }
        for (const id of recht) {
          aanhechting.set(id, { ...(aanhechting.get(id) ?? {}), [kant]: midden });
        }
        const spreid = (deel: string[], vanaf: number) => {
          const gesorteerd = [...deel].sort(
            (a, b) => overkantY(a, sleutel) - overkantY(b, sleutel),
          );
          gesorteerd.forEach((id, i) => {
            const y = vanaf + ((kaart.hoogte / 2) * (i + 1)) / (gesorteerd.length + 1);
            aanhechting.set(id, { ...(aanhechting.get(id) ?? {}), [kant]: y });
          });
        };
        // Wat van boven komt hecht in de bovenste helft, wat van onder komt in
        // de onderste: dan kruisen de lijnen elkaar niet vlak voor de rand.
        spreid(boven, kaart.y);
        spreid(onder, midden);
      }
    };
    // Alleen de INKOMENDE kant wordt verdeeld. Wat een stap verlaat vertrekt
    // uit één punt — bij een beslisstap zijn dat de takken, en die herken je
    // aan hun label ("ja" / "nee"), niet aan hun vertrekpunt.
    verdeel(inkomend, "in");

    /**
     * De plaatsing zoals dit canvas 'm vroeger afleidde: baan uit de
     * toewijzing, kolom uit de stroomdiepte. Niet meer wat je ziet, maar nog
     * wél wat de knop "schik de stappen" toepast — en wat een diagram van
     * vóór de omzetting eenmalig krijgt, zodat er niets verspringt.
     */
    const afgeleideKolom = kolommen(
      [...stappen.keys()],
      stromen.map(
        (s2) => [s2.relation.sourceConceptId, s2.relation.targetConceptId] as [string, string],
      ),
    );
    const bezet = new Map<string, number>();
    const afgeleidePlek = new Map<string, Plek>();
    for (const kaart of kaarten.values()) {
      const rij = baanVanConcept.get(kaart.concept.id) ?? 0;
      const k = afgeleideKolom.get(kaart.concept.id) ?? 0;
      const sleutel = `${rij}:${k}`;
      const n = bezet.get(sleutel) ?? 0;
      bezet.set(sleutel, n + 1);
      afgeleidePlek.set(kaart.sleutel, {
        x: MARGE + kopBreedte + k * KOLOM_AFSTAND,
        y:
          (baanToppen[rij] ?? RASTER_TOP) +
          ((baanHoogtes[rij] ?? BAAN_HOOGTE) - kaart.hoogte) / 2 +
          n * 14,
      });
    }

    /**
     * Het kader om een stap die in meer dan één baan ligt (§11a punt 6).
     *
     * Sharp tekent zo'n samenwerkende stap als één blok over de banen heen;
     * bij ons blijven het losse kaarten — anders zou je 'm niet per baan
     * kunnen verslepen — met een omhullend kader dat laat zien dat het één
     * stap is.
     */
    const kaders: { conceptId: string; x: number; y: number; breedte: number; hoogte: number }[] =
      [];
    const perStap = new Map<string, typeof kaarten extends Map<string, infer V> ? V[] : never>();
    for (const kaart of kaarten.values()) {
      const lijst = perStap.get(kaart.concept.id) ?? [];
      lijst.push(kaart);
      perStap.set(kaart.concept.id, lijst);
    }
    for (const [conceptId, lijst] of perStap) {
      if (lijst.length < 2) continue;
      const x = Math.min(...lijst.map((k) => k.x));
      const y = Math.min(...lijst.map((k) => k.y));
      const rechts = Math.max(...lijst.map((k) => k.x + k.breedte));
      const onder = Math.max(...lijst.map((k) => k.y + k.hoogte));
      kaders.push({
        conceptId,
        x: x - 6,
        y: y - 6,
        breedte: rechts - x + 12,
        hoogte: onder - y + 12,
      });
    }

    /** In welke baan ligt deze kaart, en waar staat 'ie dan verticaal? */
    const rijVanKaart = (y: number, hoogte: number) => {
      const midden = y + hoogte / 2;
      for (let i = 0; i < baanToppen.length; i += 1) {
        const top = baanToppen[i] ?? RASTER_TOP;
        if (midden >= top && midden < top + (baanHoogtes[i] ?? BAAN_HOOGTE)) return i;
      }
      return midden < RASTER_TOP ? -1 : baanToppen.length;
    };
    const middenVanBaan = (rij: number, hoogte: number) =>
      (baanToppen[rij] ?? RASTER_TOP) + ((baanHoogtes[rij] ?? BAAN_HOOGTE) - hoogte) / 2;

    const rechterrand = Math.max(
      MARGE * 2 + kopBreedte + KOLOM_AFSTAND,
      ...[...kaarten.values()].map((k) => k.x + k.breedte + MARGE),
    );
    return {
      banen: gesorteerdeBanen,
      baanVanConcept,
      kaarten,
      eersteVoorkomen,
      stappen,
      stromen,
      knikken,
      aanhechting,
      afgeleidePlek,
      /** Aantal kaarten dat nog nooit een eigen plek kreeg. */
      ongeplaatst,
      kopBreedte,
      kaders,
      rijVanKaart,
      middenVanBaan,
      baanHoogtes,
      baanToppen,
      onderkantBanen,
      breedte: rechterrand + KOLOM_AFSTAND,
      // Eén baanhoogte extra onder de laatste baan: de strook waar je een
      // nieuwe uitvoerder neerzet of een stap losmaakt. Zonder die ruimte
      // houdt het vlak op bij de laatste baan en valt een drop daar buiten
      // elke dropzone.
      hoogte: onderkantBanen + BAAN_HOOGTE + MARGE,
      aantalOverdrachten: stromen.filter((s2) => s2.overdracht).length,
    };
  }, [revision, diagram]);

  /** Zie `gekozenSleutelsRef`: de toets-afhandeling leest het model via deze. */
  const modelRef = useRef(model);
  modelRef.current = model;

  /**
   * De aangewezen voorkomens. Eén klik vervangt de selectie, Cmd/Ctrl/Shift
   * voegt toe of haalt weg — zoals op het gewone canvas, zodat de werkbalk
   * er meerdere tegelijk dezelfde maat of vorm kan geven.
   */
  const [selectie, setSelectie] = useState<string[]>([]);

  /**
   * Welk VOORKOMEN is aangewezen. Staat er maar één van dit concept, dan is
   * dat 'ie; staan er meer, dan wijst `selectedInstanceId` het aan — net als
   * op het gewone canvas.
   */
  const gekozenSleutel = (() => {
    if (selectedInstanceId && model.kaarten.has(selectedInstanceId)) return selectedInstanceId;
    if (!selectedConceptId) return null;
    return model.eersteVoorkomen.get(selectedConceptId) ?? null;
  })();

  /** Alles wat nu is aangewezen: de meervoudige selectie, of anders de ene. */
  const gekozenSleutels = selectie.length > 0 ? selectie : gekozenSleutel ? [gekozenSleutel] : [];

  // De toets-afhandeling hangt aan `window` en zou anders de stand van haar
  // eerste render vasthouden. Via refs leest 'ie wat er nú staat.
  const gekozenSleutelsRef = useRef(gekozenSleutels);
  gekozenSleutelsRef.current = gekozenSleutels;
  const hernoemtRef = useRef<string | null>(null);

  /** Klik op een kaart: vervangen, of met de modifier erbij/eraf. */
  const kiesKaart = (
    sleutel: string,
    conceptId: string,
    e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean },
  ) => {
    const erbij = e.metaKey || e.ctrlKey || e.shiftKey;
    setSelectie((huidig) => {
      if (!erbij) return [sleutel];
      return huidig.includes(sleutel) ? huidig.filter((k) => k !== sleutel) : [...huidig, sleutel];
    });
    // Het eerst aangewezen object is de maatstaf voor gelijke maat en
    // uitlijnen — hetzelfde anker als op het gewone canvas.
    //
    // Het anker wordt NA `selectInstance` gezet en bij een bij-selectie
    // opnieuw: die actie wist `selectionAnchorConceptId` (het is de gewone
    // "nieuwe selectie"-weg in `ui.ts`). Deed je dat niet, dan was het anker
    // vanaf de tweede Cmd-klik weg en viel gelijke maat terug op de grootste
    // van de selectie — precies het gedrag dat het anker moest vervangen.
    const ankerVoor = useUi.getState().selectionAnchorConceptId;
    selectInstance(sleutel, conceptId);
    useUi.getState().setSelectionAnchorConceptId(erbij ? ankerVoor : conceptId);
  };

  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: selecteren en het attributenpaneel openen mag; slepen,
  // neerzetten, hernoemen, wissen en de contextmenu's niet.
  const readOnly = useSession((s) => s.readOnly);
  /**
   * Een stap in een baan zetten is precies één ding: de toewijzing.
   *
   * Bestaat 'ie al, dan verhuist z'n bron; bestaat 'ie niet, dan komt 'ie
   * erbij; en de baan zonder naam betekent "haal 'm weg". Alles in één
   * ongedaan-stap, want voor de modelleur was het één sleep (§11a punt 1).
   */
  /**
   * De toewijzingen volgen de kaarten.
   *
   * Nu elke kaart een eigen voorkomen is, kan dezelfde stap in twee banen
   * liggen — en dan doen twee uitvoerders dat werk. Het model hoort dat te
   * zeggen: precies één toewijzing per baan waarin een kaart van die stap
   * ligt, niet meer en niet minder. Ligt er geen enkele kaart meer in een
   * baan, dan verdwijnt die toewijzing; ligt de stap in meer dan één baan,
   * dan is 'ie samenwerkend (§11a punt 6).
   *
   * Zo blijft het gebaar hetzelfde als op elk ander canvas — je sleept en er
   * wordt niets gevraagd — en klopt de tekening met wat eronder staat.
   */
  const synchroniseerToewijzingen = useCallback(
    async (stapId: string, banen: readonly (string | null)[]) => {
      if (!workspaceId || !branchId) return;
      const ws = useWorkspaceData.getState();
      const gewenst = new Set(banen.filter((b): b is string => b !== null));
      const bestaande = ws.store
        .listRelations()
        .filter((r) => !r.tombstoneInBranch && isBaanKoppeling(r) && r.targetConceptId === stapId);
      const heeftAl = new Set(bestaande.map((r) => r.sourceConceptId));
      const overbodig = bestaande.filter((r) => !gewenst.has(r.sourceConceptId));
      const missend = [...gewenst].filter((b) => !heeftAl.has(b));
      if (overbodig.length === 0 && missend.length === 0) return;
      ws.beginUserUndoBatch();
      try {
        for (const baanId of missend) {
          const bewoner = ws.store.getConcept(baanId);
          if (!bewoner) continue;
          // Een uitvoerder VOERT de stap uit (Assignment); een proces OMVAT
          // 'm (Aggregation met «Hoort bij proces»). Zie `koppelingVoor`.
          const koppeling = koppelingVoor(bewoner);
          await ws.connectConcepts(
            workspaceId,
            branchId,
            baanId,
            stapId,
            koppeling.typeId,
            koppeling.stereotypes.length > 0 ? koppeling.stereotypes : undefined,
            {
              // De eerste koppeling is de primaire; wie erbij komt ondersteunt.
              collaboration_role:
                heeftAl.size === 0 && missend[0] === baanId ? "primary" : "supporting",
            },
          );
          heeftAl.add(baanId);
        }
        for (const r of overbodig) {
          await ws.removeRelation(workspaceId, branchId, r.id);
        }
        // Meer dan één baan = samenwerkende stap. Dat staat op de stap zelf,
        // zodat de afleiding van een overdracht het hele gezelschap meeneemt.
        const stap = ws.store.getConcept(stapId);
        const samen = gewenst.size > 1;
        if (stap && (stap.attributes?.is_collaborative === true) !== samen) {
          await ws.patchConcept(workspaceId, branchId, stapId, {
            attributes: { ...stap.attributes, is_collaborative: samen },
          });
        }
      } finally {
        ws.endUserUndoBatch("swimlane.assign");
      }
    },
    [workspaceId, branchId],
  );

  /**
   * In welke banen liggen de kaarten van deze stap? De baan volgt uit de
   * hoogte waarop een kaart ligt; `verplaatst` overschrijft dat voor de kaart
   * die zojuist is versleept of neergezet.
   */
  /**
   * Leg vast in welke baan dit voorkomen ligt.
   *
   * Hoort bij élke plaatsing en élke verplaatsing: de y zegt wáár 'ie staat,
   * dit zegt waar 'ie BIJ HOORT. Zonder die tweede vraag verhuist een kaart
   * stilletjes naar de buurman zodra een baan van hoogte verandert.
   */
  const legBaanVast = useCallback(
    (sleutel: string, y: number, hoogte: number) => {
      if (!workspaceId || !branchId) return;
      const rij = modelRef.current.rijVanKaart(y, hoogte);
      const baanId = modelRef.current.banen[rij]?.id ?? null;
      useWorkspaceData.getState().setSwimlaneLaneOnCanvas(workspaceId, branchId, sleutel, baanId);
    },
    [workspaceId, branchId],
  );

  const banenVanStap = useCallback(
    (stapId: string, verplaatst?: { sleutel: string; y: number } | null) => {
      const uit: (string | null)[] = [];
      const baanBijY = (y: number, hoogte: number) =>
        model.banen[model.rijVanKaart(y, hoogte)]?.id ?? null;
      let gezien = false;
      for (const kaart of model.kaarten.values()) {
        if (kaart.concept.id !== stapId) continue;
        if (verplaatst && kaart.sleutel === verplaatst.sleutel) {
          gezien = true;
          uit.push(baanBijY(verplaatst.y, kaart.hoogte));
          continue;
        }
        uit.push(baanBijY(kaart.y, kaart.hoogte));
      }
      // Een kaart die nog niet in het model zit (zojuist geplaatst) telt mee.
      if (verplaatst && !gezien) uit.push(baanBijY(verplaatst.y, KAART_HOOGTE));
      return uit;
    },
    [model],
  );

  /**
   * De baan aan iemand anders koppelen (§11a punt 5).
   *
   * De baan-toewijzing *is* de Assignment, dus dit verlegt alle toewijzingen
   * die de stappen in die baan houden. Geen dialoog, geen verweesde stappen —
   * behalve waar de modelleur met `explicit_assignment_override` heeft
   * vastgelegd dat een stap bewust een andere uitvoerder heeft dan de baan
   * waarin 'ie staat. Blijft er zo iemand achter, dan houdt de oude eigenaar
   * z'n baan; blijft er niets, dan verdwijnt die baan van het diagram.
   *
   * Alles in één ongedaan-stap, want voor de modelleur was het één sleep.
   */
  /**
   * Trek een kaart recht ten opzichte van z'n buren.
   *
   * Een stroom hecht aan het midden van een kaart. Liggen twee kaarten een
   * paar punten uit elkaar, dan loopt de lijn ertussen zichtbaar scheef —
   * en dat valt juist op bij een samenvoeging of vertakking, want die is
   * klein en staat midden tussen twee stappen in. Met de hand op de punt
   * nauwkeurig slepen is geen werk dat een tekenprogramma aan de modelleur
   * hoort te laten.
   *
   * Vandaar: komt het midden van wat je sleept binnen `MAGNEET` punten van
   * het midden van een andere kaart, dan gaat 'ie daar precies op staan. Per
   * as apart, zodat je horizontaal kunt uitlijnen zonder verticaal vast te
   * zitten. Buiten dat bereik verandert er niets — de magneet corrigeert, hij
   * stuurt niet.
   */
  const magnetisch = useCallback(
    (
      plek: Plek,
      kaart: { sleutel: string; breedte: number; hoogte: number },
      /**
       * Hoe ver de magneet reikt. Een vertakking krijgt een ruimer bereik
       * (`MAGNEET_PUNT`): die hoort ÓP de stroomlijn te liggen en heeft geen
       * eigen hoogte om te verdedigen, dus daar is vastklikken bijna altijd
       * wat je bedoelde. Bij een kaart is acht punten genoeg — die wil je ook
       * bewust naast elkaar kunnen zetten.
       */
      bereik = MAGNEET,
    ): Plek => {
      let { x, y } = plek;
      const middenX = x + kaart.breedte / 2;
      const middenY = y + kaart.hoogte / 2;
      let besteX: number | null = null;
      let besteY: number | null = null;
      // Het hart van de baan telt als buur. Zet je twee kaarten netjes in het
      // midden, dan hoort de lijn ertussen kaarsrecht te lopen — ook als ze
      // niet even hoog zijn, wat tussen een stap (62) en een samenvoeging
      // (30) altijd het geval is. Zonder deze regel moet je die stip op de
      // punt nauwkeurig neerzetten om dat voor elkaar te krijgen.
      const rij = model.rijVanKaart(y, kaart.hoogte);
      if (rij >= 0 && rij < Math.max(1, model.banen.length)) {
        const hart = model.middenVanBaan(rij, kaart.hoogte) + kaart.hoogte / 2;
        if (Math.abs(hart - middenY) <= bereik) besteY = hart;
      }
      for (const ander of model.kaarten.values()) {
        if (ander.sleutel === kaart.sleutel) continue;
        const aX = ander.x + ander.breedte / 2;
        const aY = ander.y + ander.hoogte / 2;
        if (Math.abs(aX - middenX) <= bereik) {
          if (besteX === null || Math.abs(aX - middenX) < Math.abs(besteX - middenX)) besteX = aX;
        }
        if (Math.abs(aY - middenY) <= bereik) {
          if (besteY === null || Math.abs(aY - middenY) < Math.abs(besteY - middenY)) besteY = aY;
        }
      }
      if (besteX !== null) x = Math.round(besteX - kaart.breedte / 2);
      if (besteY !== null) y = Math.round(besteY - kaart.hoogte / 2);
      return { x, y };
    },
    [model],
  );

  const wisselBaanEigenaar = useCallback(
    async (oudeEigenaarId: string, nieuweEigenaarId: string) => {
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;
      const nieuwe = ws.store.getConcept(nieuweEigenaarId);
      // Tweede slot naast de drop-handler: deze wissel verwijdert het
      // voorkomen van de oude bewoner, en dat mag nooit gebeuren door iets
      // wat helemaal geen baan kan zijn.
      if (!nieuwe || !isBaanBewoner(nieuwe)) return;
      const viewState = ws.store.getDiagram(diagramId)?.viewState ?? {};
      const opDiagram = Object.entries(viewState)
        .filter(([, ve]) => !ve.hidden)
        .map(([sleutel, ve]) => ve.ref ?? sleutel);
      const { verhuist, blijft } = toewijzingenBijBaanwissel(
        oudeEigenaarId,
        ws.store.listConcepts().filter((c) => !c.tombstoneInBranch),
        ws.store.listRelations().filter((r) => !r.tombstoneInBranch),
        opDiagram,
      );
      ws.beginUserUndoBatch();
      try {
        if (!opDiagram.includes(nieuweEigenaarId)) {
          ws.placeExistingConceptOnDiagram(workspaceId, branchId, nieuweEigenaarId, {
            x: 0,
            y: 0,
          });
        }
        const nieuweKoppeling = koppelingVoor(nieuwe);
        for (const relId of verhuist) {
          const r = ws.store.getRelation(relId);
          if (!r) continue;
          if (r.typeId === nieuweKoppeling.typeId) {
            await ws.patchRelation(workspaceId, branchId, relId, {
              source_concept_id: nieuweEigenaarId,
              change_reason: "swimlane-owner-changed",
            });
            continue;
          }
          // Van een uitvoerder naar een proces (of andersom) verandert de
          // relatie van soort: een Assignment van een proces bestaat niet in
          // ArchiMate. Dan een nieuwe van de juiste soort, en de oude weg.
          await ws.connectConcepts(
            workspaceId,
            branchId,
            nieuweEigenaarId,
            r.targetConceptId,
            nieuweKoppeling.typeId,
            nieuweKoppeling.stereotypes.length > 0 ? nieuweKoppeling.stereotypes : undefined,
            r.attributes?.collaboration_role
              ? { collaboration_role: r.attributes.collaboration_role }
              : undefined,
          );
          await ws.removeRelation(workspaceId, branchId, relId);
        }
        if (blijft.length === 0) {
          const sleutel = Object.entries(viewState).find(
            ([k, ve]) => (ve.ref ?? k) === oudeEigenaarId,
          )?.[0];
          if (sleutel) ws.removeViewElementOnCanvas(workspaceId, branchId, diagramId, sleutel);
        }
      } finally {
        ws.endUserUndoBatch("swimlane.changeOwner");
      }
    },
    [workspaceId, branchId],
  );

  /**
   * Een verse baan hoort onderaan, niet op de plek die z'n naam 'm geeft.
   * Daarvoor moet de volgorde die de modelleur nú ziet worden vastgelegd —
   * anders staat de nieuwe baan met z'n plek vóór alles wat er nog geen
   * heeft.
   */
  const zetNieuweBaan = useCallback(
    (bewonerId: string, bovenaan: boolean) => {
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const huidig = model.banen.map((b) => b.id).filter((id): id is string => id !== null);
      if (huidig.includes(bewonerId)) return;
      useWorkspaceData
        .getState()
        .setSwimlaneRowOrderOnCanvas(
          workspaceId,
          branchId,
          bovenaan ? [bewonerId, ...huidig] : [...huidig, bewonerId],
        );
    },
    [workspaceId, branchId, model],
  );

  /**
   * Een stroom die van baan wisselt ís een overdracht, dus krijgt 'ie het
   * stereotype «Handoff» (§11a: een overdracht wordt gezien, niet gelabeld).
   * Daarmee staan Sharp's velden — transportmiddel, vertraging, foutkans,
   * kosten — meteen in het attributenpaneel.
   *
   * Dit gebeurt op het MOMENT dat de overdracht ontstaat: bij het leggen van
   * de stroom en bij het verhuizen van een stap. Niet doorlopend — anders kun
   * je het stereotype niet meer uitzetten, want dan staat 't er bij de
   * volgende render weer op.
   */
  const markeerOverdrachten = useCallback(
    (stapId: string) => {
      if (!workspaceId || !branchId) return;
      const ws = useWorkspaceData.getState();
      const concepts = ws.store.listConcepts().filter((c) => !c.tombstoneInBranch);
      const relations = ws.store.listRelations().filter((r) => !r.tombstoneInBranch);
      for (const r of relations) {
        if (r.typeId !== "archimate.Flow" && r.typeId !== "archimate.Triggering") continue;
        if (r.sourceConceptId !== stapId && r.targetConceptId !== stapId) continue;
        if ((r.stereotypes ?? []).includes("sharp.Handoff")) continue;
        if (!isOverdracht(r, concepts, relations)) continue;
        void ws.patchRelation(workspaceId, branchId, r.id, {
          stereotypes: [...(r.stereotypes ?? []), "sharp.Handoff"],
        });
      }
    },
    [workspaceId, branchId],
  );

  /**
   * Een baan groeit mee met wat je erin legt.
   *
   * De standaardhoogte past één rij kaarten. Leg je er een tweede onder, dan
   * hoort de baan hoger te worden in plaats van de kaart terug te duwen —
   * dat is wat je van slepen verwacht. De baan waarin het MIDDEN van de kaart
   * valt is de baan; die rekt op tot de kaart er helemaal in past.
   */
  const laatBaanMeegroeien = useCallback(
    (rij: number, onderkantKaart: number) => {
      if (!workspaceId || !branchId) return;
      const baan = model.banen[rij];
      if (!baan?.id) return;
      const top = model.baanToppen[rij] ?? RASTER_TOP;
      const hoogte = model.baanHoogtes[rij] ?? BAAN_HOOGTE;
      const nodig = onderkantKaart + 10 - top;
      if (nodig <= hoogte) return;
      useWorkspaceData
        .getState()
        .setSwimlaneLaneHeightOnCanvas(workspaceId, branchId, baan.id, Math.round(nodig));
    },
    [workspaceId, branchId, model],
  );

  /**
   * Een drop op een baan. Drie bronnen, zoals overal: een bestaand concept,
   * een selectie ervan, of een verse uit het palet. Een stap landt in de baan;
   * een uitvoerder krijgt er zelf een.
   */
  const opBaanGedropt = useCallback(
    async (
      event: DragEvent<HTMLDivElement>,
      baanId: string | null,
      opts?: { baanRelatief?: boolean; bovenaan?: boolean },
    ) => {
      event.preventDefault();
      event.stopPropagation();
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const ws = useWorkspaceData.getState();
      const diagramId = ws.activeDiagramId;
      if (!diagramId) return;

      /**
       * De kolom waar de muis losliet. Alleen gebruikt wanneer die ánders is
       * dan waar de stap al stond: verticaal naar een andere baan slepen
       * hoort de afgeleide kolom niet te bevriezen — dat zou de hele
       * automatische plaatsing uitzetten bij de eerste de beste verhuizing.
       *
       * React nult `currentTarget` zodra de handler terugkeert, en deze
       * functie draait ná een await. Vandaar dat de baan en de muispositie
       * hierboven meteen worden vastgelegd.
       */
      const baanEl = event.currentTarget;
      const baanRect = baanEl.getBoundingClientRect();
      const baanBreedte = baanEl.clientWidth;
      const losliet = event.clientX;
      const losllietY = event.clientY;
      // De baanstrook begint op MARGE + rij * BAAN_HOOGTE; die offset hebben
      // we nodig om van baan-coordinaten naar canvas-coordinaten te komen.
      const baanTop = Number.parseFloat(baanEl.style.top || "0");

      /**
       * Waar de muis losliet, in canvas-coordinaten — dát is waar de kaart
       * komt te liggen. De plaatsing wordt opgeslagen, niet afgeleid.
       */
      const plekUitDrop = (): { x: number; y: number } | null => {
        if (baanRect.width === 0) return null;
        const schaal = baanBreedte / baanRect.width;
        const x = (losliet - baanRect.left) * schaal + (opts?.baanRelatief === false ? 0 : MARGE);
        const y =
          (losllietY - baanRect.top) * schaal + (opts?.baanRelatief === false ? 0 : baanTop);
        return {
          x: Math.max(MARGE + model.kopBreedte, Math.round(x - KAART_BREEDTE / 2)),
          y: Math.max(RASTER_TOP, Math.round(y - KAART_HOOGTE / 2)),
        };
      };

      const plaatsEnZet = async (conceptId: string) => {
        const concept = ws.store.getConcept(conceptId);
        if (!concept) return;
        const staatEr = Object.entries(ws.store.getDiagram(diagramId)?.viewState ?? {}).some(
          ([sleutel, ve]) => (ve.ref ?? sleutel) === conceptId,
        );
        // Alleen een UITVOERDER opent een baan. Een procesresultaat, een
        // gebeurtenis of een werkobject is geen baan maar een kaart erin —
        // die kwam anders op (0,0) achter de titelbalk terecht.
        if (isBaanBewoner(concept)) {
          if (!staatEr) {
            ws.placeExistingConceptOnDiagram(workspaceId, branchId, conceptId, { x: 0, y: 0 });
            zetNieuweBaan(conceptId, opts?.bovenaan === true);
          }
          return;
        }
        const stap = isStap(concept);
        // Een kaart uit de Explorer landt op de plek waar je 'm loslaat. Stond
        // 'ie er al, dan is dit een TWEEDE VOORKOMEN — hetzelfde ding, een
        // eigen kaart — precies zoals op elk ander canvas.
        const losgelaten = plekUitDrop() ?? { x: MARGE + model.kopBreedte, y: RASTER_TOP };
        // Voorkomens van dezelfde stap staan boven elkaar: het is één moment
        // in de tijd, alleen in een andere baan. De hoogte waar je loslaat
        // telt dus wél, de horizontale plek niet. Voor een resultaat geldt dat
        // niet — dat is geen moment in de keten maar een uitkomst.
        const eerste = stap
          ? [...model.kaarten.values()].find((k) => k.concept.id === conceptId)
          : undefined;
        const plek = { x: eerste ? eerste.x : losgelaten.x, y: losgelaten.y };
        const nieuweSleutel = ws.placeExistingConceptOnDiagram(
          workspaceId,
          branchId,
          conceptId,
          plek,
        );
        // Hetzelfde uiterlijk als de kaart die er al stond: maat, opmaak,
        // kleuren. Het is dezelfde stap, dus een tweede kaart die er anders
        // uitziet zou suggereren dat er iets anders staat.
        if (eerste && nieuweSleutel && nieuweSleutel !== eerste.sleutel) {
          const bron = diagram.viewState?.[eerste.sleutel];
          ws.resizeNodeOnCanvas(workspaceId, branchId, nieuweSleutel, {
            width: eerste.breedte,
            height: eerste.hoogte,
          });
          if (bron?.nodeStyle) {
            ws.setNodeStyleOnCanvas(workspaceId, branchId, nieuweSleutel, bron.nodeStyle);
          }
        }
        if (nieuweSleutel) legBaanVast(nieuweSleutel, plek.y, KAART_HOOGTE);
        laatBaanMeegroeien(model.rijVanKaart(plek.y, KAART_HOOGTE), plek.y + KAART_HOOGTE);
        if (!stap) return;
        await synchroniseerToewijzingen(
          conceptId,
          banenVanStap(conceptId, { sleutel: "__nieuw", y: plek.y }),
        );
      };

      const bestaand = event.dataTransfer.getData("application/ae-existing-concept-id");
      if (bestaand) {
        await plaatsEnZet(bestaand);
        return;
      }

      const bulkRaw = event.dataTransfer.getData("application/ae-existing-concept-ids");
      if (bulkRaw) {
        let ids: string[] = [];
        try {
          const geparsed: unknown = JSON.parse(bulkRaw);
          if (Array.isArray(geparsed))
            ids = geparsed.filter((v): v is string => typeof v === "string");
        } catch {
          ids = [];
        }
        for (const id of ids) await plaatsEnZet(id);
        return;
      }

      const typeId = event.dataTransfer.getData("application/ae-type-id");
      if (!typeId) return;
      const droppedName = readDroppedTypeName(event.dataTransfer);
      let stereotypes: string[] | undefined;
      const stereoRaw = event.dataTransfer.getData("application/ae-default-stereotypes");
      if (stereoRaw) {
        try {
          const geparsed: unknown = JSON.parse(stereoRaw);
          if (
            Array.isArray(geparsed) &&
            geparsed.every((v): v is string => typeof v === "string")
          ) {
            stereotypes = geparsed;
          }
        } catch {
          // een kapotte payload is geen reden om de drop te laten mislukken
        }
      }
      let attributen: Record<string, unknown> | undefined;
      const attrRaw = event.dataTransfer.getData("application/ae-default-attributes");
      if (attrRaw) {
        try {
          attributen = JSON.parse(attrRaw) as Record<string, unknown>;
        } catch {
          // idem
        }
      }
      const elementType = getRegistry().getElementType(typeId);
      const naamVoorNieuw = droppedName
        ? droppedName
        : {
            nl: elementType?.label?.nl ?? "Nieuwe stap",
            en: elementType?.label?.en ?? "New step",
          };
      const gemaakt = await ws.addConceptOnCanvas(
        workspaceId,
        branchId,
        typeId,
        naamVoorNieuw,
        { x: 0, y: 0 },
        attributen,
        stereotypes,
      );
      if (!gemaakt) return;
      if (isBaanBewoner(gemaakt)) {
        zetNieuweBaan(gemaakt.id, opts?.bovenaan === true);
        return;
      }
      // Al het andere is een kaart en landt waar je 'm loslaat: een stap, maar
      // net zo goed een procesresultaat, een gebeurtenis of een vertakking.
      const vertakking = vormVan(gemaakt) === "vertakking";
      const plek = plekUitDrop();
      if (plek) {
        // Een vertakking is een punt, geen vlak. `plekUitDrop` centreert op de
        // maat van een gewone kaart, dus zonder deze correctie ligt 'ie een
        // halve kaart links-boven van waar je losliet.
        const maat = vertakking
          ? { breedte: VERTAKKING_MAAT, hoogte: VERTAKKING_MAAT }
          : { breedte: KAART_BREEDTE, hoogte: KAART_HOOGTE };
        const raak = magnetisch(
          vertakking
            ? {
                x: plek.x + (KAART_BREEDTE - VERTAKKING_MAAT) / 2,
                y: plek.y + (KAART_HOOGTE - VERTAKKING_MAAT) / 2,
              }
            : plek,
          { sleutel: "__nieuw", ...maat },
          vertakking ? MAGNEET_PUNT : MAGNEET,
        );
        ws.moveConceptOnCanvas(workspaceId, branchId, gemaakt.id, raak);
        legBaanVast(gemaakt.id, raak.y, maat.hoogte);
        if (vertakking) {
          ws.resizeNodeOnCanvas(workspaceId, branchId, gemaakt.id, {
            width: VERTAKKING_MAAT,
            height: VERTAKKING_MAAT,
          });
        }
        laatBaanMeegroeien(model.rijVanKaart(raak.y, KAART_HOOGTE), raak.y + KAART_HOOGTE);
      }
      // Alleen een stap krijgt een uitvoerder. Een resultaat ligt in de baan
      // zonder dat iemand 'm uitvoert.
      if (isStap(gemaakt)) await synchroniseerToewijzingen(gemaakt.id, [baanId]);
    },
    [
      workspaceId,
      branchId,
      zetNieuweBaan,
      magnetisch,
      synchroniseerToewijzingen,
      banenVanStap,
      model,
      laatBaanMeegroeien,
    ],
  );

  /**
   * Het stuurpunt slepen. De knik wordt opgeslagen ten opzichte van de route
   * (`langs`/`opzij`), niet als punt — zie `SwimlaneKnik`. Tijdens het slepen
   * volgt de lijn direct mee; pas bij loslaten gaat 'ie naar de store, zodat
   * er één ongedaan-stap ontstaat en niet één per muisbeweging.
   */
  const sleepKnik = useCallback(
    (
      event: ReactPointerEvent<SVGCircleElement>,
      relationId: string,
      start: { x: number; y: number },
      eind: { x: number; y: number },
    ) => {
      event.stopPropagation();
      event.preventDefault();
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const doel = event.currentTarget;
      const svg = doel.ownerSVGElement;
      if (!svg) return;
      doel.setPointerCapture(event.pointerId);
      const naarCanvas = (e: { clientX: number; clientY: number }) => {
        const r = svg.getBoundingClientRect();
        // De ZoomPan schaalt het canvas; reken terug naar modelcoördinaten.
        const schaal = r.width === 0 ? 1 : svg.clientWidth / r.width;
        return { x: (e.clientX - r.left) * schaal, y: (e.clientY - r.top) * schaal };
      };
      let laatste: SwimlaneKnik | null = null;
      const beweeg = (e: PointerEvent) => {
        laatste = knikUitPunt(start, eind, naarCanvas(e));
        doel.setAttribute("cx", String(knikStuurpunt(start, eind, laatste).x));
        doel.setAttribute("cy", String(knikStuurpunt(start, eind, laatste).y));
      };
      const stop = () => {
        doel.removeEventListener("pointermove", beweeg);
        doel.removeEventListener("pointerup", stop);
        doel.removeEventListener("pointercancel", stop);
        if (laatste) {
          useWorkspaceData
            .getState()
            .setSwimlaneKnikOnCanvas(workspaceId, branchId, relationId, laatste);
        }
      };
      doel.addEventListener("pointermove", beweeg);
      doel.addEventListener("pointerup", stop);
      doel.addEventListener("pointercancel", stop);
    },
    [workspaceId, branchId],
  );

  /**
   * Hernoemen op het canvas zelf (F2, of dubbelklik op de naam).
   *
   * `Editor.tsx` stuurt `ae:rename-concept` met het geselecteerde concept;
   * `ConceptNode` vangt dat op het gewone canvas op. Dit canvas heeft een
   * eigen render-pad en hoorde dus z'n eigen brug te hebben — zonder deze
   * deed F2 hier niets, terwijl de baankop de enige plek is waar de naam van
   * een uitvoerder te zien is.
   *
   * Het slot pakken we ernaast en niet ervoor, net als daar: hernoemen hoort
   * ogenblikkelijk te zijn, en wachten op een netwerkaanroep kost iedereen
   * altijd de eerste aanslagen.
   */
  const [hernoemt, setHernoemt] = useState<string | null>(null);
  // Tijdens het hernoemen doen Delete en de pijltjes niets op het canvas —
  // dan typ je in het naamveld.
  hernoemtRef.current = hernoemt;
  const [concept_, setConcept_] = useState("");
  const slotVast = useRef(false);

  const beginHernoemen = useCallback(
    (sleutel: string, conceptId: string, huidige: string) => {
      if (useSession.getState().readOnly) return;
      setConcept_(huidige);
      setHernoemt(sleutel);
      void claimObjectLock(conceptId).then((geweigerd) => {
        if (!geweigerd) {
          slotVast.current = true;
          return;
        }
        setHernoemt(null);
        useWorkspaceData.setState({
          lastError: geweigerd.byBatch
            ? t("locks.heldByBatch")
            : t("locks.heldBy", { holder: geweigerd.holder }),
        });
      });
    },
    [t],
  );

  const stopHernoemen = useCallback((_sleutel: string, conceptId: string) => {
    setHernoemt(null);
    if (!slotVast.current) return;
    slotVast.current = false;
    releaseObjectLock(conceptId);
  }, []);

  const bewaarNaam = useCallback(
    async (sleutel: string, conceptId: string, tekst: string) => {
      stopHernoemen(sleutel, conceptId);
      if (!workspaceId || !branchId) return;
      await hernoemConcept({
        workspaceId,
        branchId,
        conceptId,
        langKey: lang === "nl" ? "nl" : "en",
        nieuweNaam: tekst,
      });
    },
    [workspaceId, branchId, lang, stopHernoemen],
  );

  useEffect(() => {
    const opHernoem = (e: Event) => {
      const detail = (e as CustomEvent<{ conceptId: string }>).detail;
      if (!detail) return;
      const c = useWorkspaceData.getState().store.getConcept(detail.conceptId);
      if (!c) return;
      // Alleen wat op dit diagram staat: een selectie uit de Explorer hoort
      // hier geen invoerveld te openen dat nergens zichtbaar is.
      const sleutel =
        (gekozenSleutel && model.kaarten.get(gekozenSleutel)?.concept.id === detail.conceptId
          ? gekozenSleutel
          : null) ??
        model.eersteVoorkomen.get(detail.conceptId) ??
        (model.banen.some((b) => b.id === detail.conceptId) ? detail.conceptId : null);
      if (!sleutel) return;
      beginHernoemen(sleutel, detail.conceptId, kaartNaam(c));
    };
    window.addEventListener("ae:rename-concept", opHernoem);
    return () => window.removeEventListener("ae:rename-concept", opHernoem);
  }, [model, lang, beginHernoemen, gekozenSleutel]);

  /**
   * Het pad van een stroom.
   *
   * De routing uit het contextmenu geldt hier net zo goed als op het gewone
   * canvas — dezelfde functies uit React Flow, zodat "vloeiend" er ook echt
   * vloeiend uitziet. Zonder gekozen routing houdt dit canvas z'n eigen
   * boog: die draagt het sleepbare knikpunt, en dat is wat de modelleur hier
   * het vaakst gebruikt.
   */
  const stroomPad = (
    routing: string | undefined,
    start: { x: number; y: number },
    eind: { x: number; y: number },
    stuur: { x: number; y: number },
    terug = false,
  ): string => {
    const gemeen = {
      sourceX: start.x,
      sourceY: start.y,
      // Een terugkoppeling verlaat de kaart aan de BOVENkant en komt er aan
      // de bovenkant weer in. Vertrok 'ie rechts en kwam 'ie links binnen —
      // de regel voor alles wat vooruit loopt — dan liep de lijn dwars door
      // elke kaart ertussen heen, want het doel ligt links van de bron.
      sourcePosition: terug ? Position.Top : Position.Right,
      targetX: eind.x,
      targetY: eind.y,
      targetPosition: terug ? Position.Top : Position.Left,
    };
    if (routing === "straight") return getStraightPath(gemeen)[0];
    if (routing === "bezier") return getBezierPath(gemeen)[0];
    if (routing === "step") return getSmoothStepPath({ ...gemeen, borderRadius: 0 })[0];
    // "manual" is de eigen boog met het sleepbare knikpunt; al het andere —
    // inclusief niets gekozen — is smoothstep, net als op het gewone canvas.
    if (routing === "manual") {
      return `M ${start.x} ${start.y} Q ${stuur.x} ${stuur.y} ${eind.x} ${eind.y}`;
    }
    return getSmoothStepPath(gemeen)[0];
  };

  /** Het invoerveld dat tijdens het hernoemen in de plaats komt van de naam. */
  const naamVeld = (sleutel: string, conceptId: string, className: string) => (
    <input
      // biome-ignore lint/a11y/noAutofocus: F2 hoort meteen te kunnen typen
      autoFocus
      className={`select-text ${className}`}
      value={concept_}
      onChange={(e) => setConcept_(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") void bewaarNaam(sleutel, conceptId, concept_);
        else if (e.key === "Escape") stopHernoemen(sleutel, conceptId);
      }}
      onBlur={() => void bewaarNaam(sleutel, conceptId, concept_)}
    />
  );

  /**
   * Een baan boven of onder een andere zetten.
   *
   * Slepen is ook hier het gebaar: pak de kop en laat 'm los op de baan waar
   * 'ie heen moet. De hele volgorde wordt daarna opnieuw genummerd, zodat
   * banen die nog nooit verplaatst zijn hun plek houden in plaats van naar
   * achteren te zakken.
   */
  const verplaatsBaan = useCallback(
    (bewonerId: string, naarBewonerId: string, eronder: boolean) => {
      if (!workspaceId || !branchId || bewonerId === naarBewonerId) return;
      if (useSession.getState().readOnly) return;
      const huidig = model.banen.map((b) => b.id).filter((id): id is string => id !== null);
      const van = huidig.indexOf(bewonerId);
      if (van === -1 || !huidig.includes(naarBewonerId)) return;
      const nieuw = [...huidig];
      nieuw.splice(van, 1);
      // De doel-index opnieuw bepalen ná het weghalen: anders klopt 'ie niet
      // meer wanneer de gesleepte baan erboven stond.
      const naar = nieuw.indexOf(naarBewonerId);
      nieuw.splice(eronder ? naar + 1 : naar, 0, bewonerId);
      useWorkspaceData.getState().setSwimlaneRowOrderOnCanvas(workspaceId, branchId, nieuw);
    },
    [workspaceId, branchId, model],
  );

  /**
   * Wat de werkbalk als selectie ziet.
   *
   * Dezelfde werkbalk als op het gewone canvas, met dezelfde begrippen: een
   * "node" is hier het voorkomen van een stap of van een baan-bewoner, met de
   * plek en de maat die dit canvas eraan geeft. Daardoor landen tekengrootte,
   * lettertype, kleuren en de maat op precies dezelfde viewState-velden — één
   * manier van werken, ongeacht het diagramtype.
   */
  const werkbalkNodes = useMemo(() => {
    const uit: Node[] = [];
    for (const kaart of model.kaarten.values()) {
      uit.push({
        id: kaart.sleutel,
        position: { x: kaart.x, y: kaart.y },
        width: kaart.breedte,
        height: kaart.hoogte,
        selected: gekozenSleutels.includes(kaart.sleutel),
        data: {},
      } as Node);
    }
    model.banen.forEach((baan, rij) => {
      if (!baan.id) return;
      uit.push({
        id: baan.id,
        position: { x: MARGE, y: model.baanToppen[rij] ?? RASTER_TOP },
        width: model.kopBreedte,
        height: BAAN_HOOGTE,
        selected: selectedConceptId === baan.id,
        data: {},
      } as Node);
    });
    return uit;
  }, [model, gekozenSleutels, selectedConceptId]);

  /**
   * De opmaak die de modelleur via de werkbalk op dit voorkomen zette.
   * Ongezet = de standaard van dit canvas, dus een diagram waar niemand aan
   * de werkbalk kwam ziet er hetzelfde uit als voorheen.
   */
  const opmaakVan = (conceptId: string): NodeStyle =>
    diagram.viewState?.[conceptId]?.nodeStyle ?? {};

  /** De tekstopmaak uit de werkbalk als stijl-object. */
  const tekstStijl = (ns: NodeStyle, standaardFont: number): CSSProperties => ({
    fontSize: ns.fontSize ?? standaardFont,
    ...(ns.fontFamily ? { fontFamily: ns.fontFamily } : {}),
    ...(ns.bold !== undefined ? { fontWeight: ns.bold ? 700 : 400 } : {}),
    ...(ns.italic ? { fontStyle: "italic" } : {}),
    ...(ns.underline ? { textDecoration: "underline" } : {}),
    ...(ns.fontColor ? { color: ns.fontColor } : {}),
    // Midden is de standaard, net als op het gewone canvas: de werkbalk
    // WIST het veld bij een klik op centreren in plaats van "center" op te
    // slaan. Met links als standaard deed die knop hier dus niets.
    textAlign: ns.labelTextAlign ?? "center",
    ...(ns.labelWrap === "hyphenate" ? { hyphens: "auto" as const } : {}),
    ...(ns.labelWrap === "break-anywhere" ? { overflowWrap: "anywhere" as const } : {}),
    ...(ns.labelOrientation === "vertical"
      ? { writingMode: "vertical-rl" as const, transform: "rotate(180deg)" }
      : {}),
  });

  /**
   * Diagrammen van vóór de omzetting hebben geen plek per kaart: die stonden
   * allemaal op (0,0) en werden bij het tekenen afgeleid. Eén keer leggen we
   * die afleiding vast, zodat er niets verspringt en de modelleur vanaf dat
   * moment zelf de plaatsing bepaalt.
   */
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    const teHoog = [...model.kaarten.values()].filter((k) => k.y < RASTER_TOP && k.y !== 0);
    if (model.ongeplaatst === 0 && teHoog.length === 0) return;
    const ws = useWorkspaceData.getState();
    ws.beginUserUndoBatch();
    try {
      for (const kaart of model.kaarten.values()) {
        if (kaart.x === 0 && kaart.y === 0) {
          const plek = model.afgeleidePlek.get(kaart.sleutel);
          if (plek) ws.moveConceptOnCanvas(workspaceId, branchId, kaart.sleutel, plek);
          continue;
        }
        // Een kaart uit de tijd vóór de titelbalk staat te hoog: het raster
        // begon toen bij de marge. Schuif 'm naar dezelfde baan onder de
        // nieuwe kop, zodat 'ie blijft waar 'ie hoorde.
        if (kaart.y < RASTER_TOP) {
          const oudeRij = Math.max(0, Math.floor((kaart.y - MARGE) / BAAN_HOOGTE));
          ws.moveConceptOnCanvas(workspaceId, branchId, kaart.sleutel, {
            x: kaart.x,
            y: model.middenVanBaan(Math.max(0, oudeRij), kaart.hoogte),
          });
        }
      }
    } finally {
      ws.endUserUndoBatch("swimlane.layout");
    }
  }, [workspaceId, branchId, model]);

  /**
   * De stappen in één keer schikken: baan uit de toewijzing, kolom uit de
   * stroomdiepte. Dat was vroeger de vaste regel; nu is het een hulpmiddel
   * dat je aanroept wanneer je het wilt, en daarna sleep je weer zelf.
   */
  const schikStappen = useCallback(() => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    const ws = useWorkspaceData.getState();
    ws.beginUserUndoBatch();
    try {
      for (const kaart of model.kaarten.values()) {
        const plek = model.afgeleidePlek.get(kaart.sleutel);
        if (plek) ws.moveConceptOnCanvas(workspaceId, branchId, kaart.sleutel, plek);
      }
    } finally {
      ws.endUserUndoBatch("swimlane.layout");
    }
  }, [workspaceId, branchId, model]);

  /**
   * Een stap staat verticaal in het midden van z'n baan.
   *
   * De hoogte waarop je 'm loslaat kiest de baan; binnen die baan hangt 'ie
   * daarna in het midden. Dat houdt het beeld rustig en zorgt dat een kaart
   * na een maatwijziging niet scheef komt te hangen — precies wat er wél
   * gebeurt als je de hoogte verandert en de bovenkant blijft staan.
   */
  /**
   * Leg van bestaande kaarten vast in welke baan ze liggen.
   *
   * Eenmalig per diagram, en het verplaatst NIETS: het schrijft alleen op wat
   * er al staat. Diagrammen van vóór `swimlaneBaanId` hebben die koppeling
   * niet, en zonder deze pas krijgen ze 'm pas zodra je elke kaart een keer
   * hebt aangeraakt — tot die tijd verhuist zo'n kaart nog steeds naar de
   * buurman als je een baan lager maakt. Wat niet netjes binnen een baan ligt
   * slaan we over; daar is geen antwoord op te geven zonder te gokken.
   */
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    const diagramId = ws.activeDiagramId;
    if (!diagramId) return;
    const viewState = ws.store.getDiagram(diagramId)?.viewState ?? {};
    for (const kaart of model.kaarten.values()) {
      if (viewState[kaart.sleutel]?.swimlaneBaanId) continue;
      if (kaart.x === 0 && kaart.y === 0) continue;
      const rij = model.rijVanKaart(kaart.y, kaart.hoogte);
      if (rij < 0 || rij >= model.banen.length) continue;
      const baanId = model.banen[rij]?.id;
      if (!baanId) continue;
      const top = model.baanToppen[rij] ?? RASTER_TOP;
      const hoogte = model.baanHoogtes[rij] ?? BAAN_HOOGTE;
      if (kaart.y < top || kaart.y + kaart.hoogte > top + hoogte) continue;
      ws.setSwimlaneLaneOnCanvas(workspaceId, branchId, kaart.sleutel, baanId);
    }
  }, [workspaceId, branchId, model]);

  useEffect(() => {
    if (!workspaceId || !branchId) return;
    // Alleen wat BUITEN z'n baan hangt wordt teruggezet. Eerder werd elke
    // kaart naar het midden getrokken, en dan kun je er geen twee onder
    // elkaar zetten — precies wat je in een baan wilt kunnen doen.
    const scheef = [...model.kaarten.values()].filter((k) => {
      if (k.x === 0 && k.y === 0) return false;
      const rij = model.rijVanKaart(k.y, k.hoogte);
      // Wat BUITEN het raster ligt blijft liggen. Het terugtrekken naar de
      // dichtstbijzijnde baan leek behulpzaam maar is het niet: dan verhuist
      // van alles waar de modelleur niet aan zat, en alles wat onder de
      // laatste baan hing belandt op één hoop in die laatste baan (gemeld
      // 2026-09-19). Zichtbaar laten staan waar het staat is eerlijker; het
      // canvas groeit eronder mee, en "Schik de stappen" is de knop waarmee
      // je het zélf laat herschikken.
      if (rij < 0 || rij >= Math.max(1, model.banen.length)) return false;
      const top = model.baanToppen[rij] ?? RASTER_TOP;
      const hoogte = model.baanHoogtes[rij] ?? BAAN_HOOGTE;
      return k.y < top + 2 || k.y + k.hoogte > top + hoogte - 2;
    });
    if (scheef.length === 0) return;
    const ws = useWorkspaceData.getState();
    ws.beginUserUndoBatch();
    try {
      for (const kaart of scheef) {
        const rij = model.rijVanKaart(kaart.y, kaart.hoogte);
        ws.moveConceptOnCanvas(workspaceId, branchId, kaart.sleutel, {
          x: kaart.x,
          y: Math.round(model.middenVanBaan(rij, kaart.hoogte)),
        });
      }
    } finally {
      ws.endUserUndoBatch("swimlane.layout");
    }
  }, [workspaceId, branchId, model]);

  /**
   * Wisselt de kop van breedte — omdat alle baannamen staand werden, of juist
   * niet meer — dan schuiven de kaarten mee. Anders blijft er een gat staan
   * ter grootte van de oude kop, of verdwijnt de eerste kolom eronder.
   */
  const vorigeKop = useRef(model.kopBreedte);
  useEffect(() => {
    const vorige = vorigeKop.current;
    vorigeKop.current = model.kopBreedte;
    if (vorige === model.kopBreedte || !workspaceId || !branchId) return;
    const verschil = vorige - model.kopBreedte;
    const ws = useWorkspaceData.getState();
    ws.beginUserUndoBatch();
    try {
      for (const kaart of model.kaarten.values()) {
        ws.moveConceptOnCanvas(workspaceId, branchId, kaart.sleutel, {
          x: Math.max(MARGE + model.kopBreedte, Math.round(kaart.x - verschil)),
          y: kaart.y,
        });
      }
    } finally {
      ws.endUserUndoBatch("swimlane.layout");
    }
  }, [workspaceId, branchId, model]);

  /**
   * Een kaart verslepen.
   *
   * De plek wordt opgeslagen, niet afgeleid: waar je 'm loslaat is waar 'ie
   * ligt — net als op elk ander canvas. De baan volgt uit de hoogte waarop
   * 'ie landt, en die baan legt de toewijzing aan (§11a punt 1). Loslaten
   * onder de laatste baan maakt 'm los van z'n uitvoerder.
   */
  const [sleept, setSleept] = useState<{ sleutel: string; x: number; y: number } | null>(null);

  const sleepKaart = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>, sleutel: string) => {
      if (event.button !== 0) return;
      // Een lezer sleept niet; de klik (selecteren) blijft gewoon werken.
      if (useSession.getState().readOnly) return;
      const kaart = model.kaarten.get(sleutel);
      if (!kaart || !workspaceId || !branchId) return;
      const doel = event.currentTarget;
      const vlak = doel.parentElement;
      if (!vlak) return;
      const startX = event.clientX;
      const startY = event.clientY;
      const schaalVan = () => {
        const r = vlak.getBoundingClientRect();
        return r.width === 0 ? 1 : vlak.clientWidth / r.width;
      };
      let laatste = { x: kaart.x, y: kaart.y };
      let versleept = false;
      /**
       * Een kaart blijft in z'n baan (H8: zet je bewust iets niet op dezelfde
       * hoogte, dan wordt de baan breder — niet: dan belandt het bij de
       * buurman). Wie 'm naar een ANDERE baan wil verhuizen houdt Alt (⌥)
       * ingedrukt. Alt en niet Cmd of Shift, want die twee voegen al toe aan
       * de selectie, en Ctrl+klik is op de Mac een rechtsklik.
       *
       * Een kaart die buiten het raster ligt heeft geen baan om in te
       * blijven; die sleep je vrij.
       */
      const startRij = kaart.rij ?? model.rijVanKaart(kaart.y, kaart.hoogte);
      const heeftBaan = startRij >= 0 && startRij < model.banen.length;
      const baanTop = heeftBaan ? (model.baanToppen[startRij] ?? RASTER_TOP) : 0;
      let metAlt = event.altKey;
      // De muis meteen vastpakken, niet pas bij de eerste beweging: zonder
      // die greep gaan de bewegingen naar wat er ónder de cursor ligt zodra
      // die de kaart verlaat, en dan stopt de sleep na een paar punten.
      // De klik blijft gewoon werken; `versleept` houdt klik en sleep uit
      // elkaar.
      doel.setPointerCapture(event.pointerId);
      const beweeg = (e: PointerEvent) => {
        const schaal = schaalVan();
        const dx = (e.clientX - startX) * schaal;
        const dy = (e.clientY - startY) * schaal;
        if (!versleept && Math.abs(dx) + Math.abs(dy) < 3) return;
        versleept = true;
        metAlt = e.altKey;
        const plek = magnetisch(
          {
            x: Math.max(MARGE + model.kopBreedte, Math.round(kaart.x + dx)),
            y: Math.max(MARGE + TITEL_HOOGTE, Math.round(kaart.y + dy)),
          },
          kaart,
          isVertakking(kaart.concept) ? MAGNEET_PUNT : MAGNEET,
        );
        // Zonder Alt niet boven de bovenrand van de eigen baan uit. Naar
        // beneden mag wel: dan groeit de baan bij het loslaten mee.
        laatste = heeftBaan && !metAlt ? { x: plek.x, y: Math.max(plek.y, baanTop + 2) } : plek;
        setSleept({ sleutel, ...laatste });
      };
      const stop = (e: PointerEvent) => {
        doel.removeEventListener("pointermove", beweeg);
        doel.removeEventListener("pointerup", stop);
        doel.removeEventListener("pointercancel", stop);
        setSleept(null);
        if (!versleept) return;
        metAlt = e.altKey || metAlt;
        const blijftInBaan = heeftBaan && !metAlt;
        const ws = useWorkspaceData.getState();
        ws.beginUserUndoBatch();
        try {
          ws.moveConceptOnCanvas(workspaceId, branchId, sleutel, laatste);
          if (blijftInBaan) {
            // Dezelfde baan als bij het begin — ook als de kaart onder de
            // onderrand is losgelaten. Die baan groeit dan mee.
            ws.setSwimlaneLaneOnCanvas(
              workspaceId,
              branchId,
              sleutel,
              model.banen[startRij]?.id ?? null,
            );
            laatBaanMeegroeien(startRij, laatste.y + kaart.hoogte);
          } else {
            legBaanVast(sleutel, laatste.y, kaart.hoogte);
            laatBaanMeegroeien(
              model.rijVanKaart(laatste.y, kaart.hoogte),
              laatste.y + kaart.hoogte,
            );
          }
          // Voorkomens van dezelfde stap blijven boven elkaar staan: schuif
          // je er één opzij, dan schuiven de anderen mee. Verticaal niet —
          // daar zit juist het verschil, want dat is hun baan.
          for (const ander of model.kaarten.values()) {
            if (ander.sleutel === sleutel || ander.concept.id !== kaart.concept.id) continue;
            if (ander.x === laatste.x) continue;
            ws.moveConceptOnCanvas(workspaceId, branchId, ander.sleutel, {
              x: laatste.x,
              y: ander.y,
            });
          }
          // De toewijzingen volgen de kaarten: deze kaart ligt nu ergens
          // anders, dus wordt opnieuw bepaald in welke banen deze stap ligt.
          //
          // Alleen voor een STAP. Een resultaat, een gebeurtenis of een
          // vertakking ligt wel ín een baan maar wordt door niemand
          // uitgevoerd, en een toewijzing daarheen is niet alleen betekenisloos
          // maar schadelijk: een junction verbindt relaties van hetzelfde
          // type, dus zodra er een Assignment aan hangt weigert de server er
          // een Flow bij (gemeten 2026-09-17).
          //
          // Bleef de kaart in z'n baan, dan verandert er aan de koppeling
          // niets en hoeft er niets gesynchroniseerd.
          if (isStap(kaart.concept) && !blijftInBaan) {
            void synchroniseerToewijzingen(
              kaart.concept.id,
              banenVanStap(kaart.concept.id, { sleutel, y: laatste.y }),
            ).then(() => markeerOverdrachten(kaart.concept.id));
          }
        } finally {
          ws.endUserUndoBatch("swimlane.move");
        }
      };
      doel.addEventListener("pointermove", beweeg);
      doel.addEventListener("pointerup", stop);
      doel.addEventListener("pointercancel", stop);
    },
    [
      workspaceId,
      branchId,
      model,
      magnetisch,
      synchroniseerToewijzingen,
      banenVanStap,
      laatBaanMeegroeien,
      markeerOverdrachten,
    ],
  );

  /**
   * Een stroom leggen door 'm te trekken.
   *
   * Hetzelfde gebaar als op het gewone canvas: pak het rondje op de rand van
   * een stap en laat los op de stap waar 't werk heen gaat. Het viewpoint
   * bepaalt welk relatietype daaruit volgt (`default_relation_type`); of 't
   * een overdracht is hoeft niemand te zeggen — dat volgt uit de banen.
   *
   * Tijdens het slepen tekent een losse SVG-lijn mee; die leeft in een state
   * zodat de lijn hertekent zonder de rest van het canvas aan te raken.
   */
  const [trekt, setTrekt] = useState<{ van: string; naar: { x: number; y: number } } | null>(null);

  /**
   * Welk relatietype hoort bij deze stroom?
   *
   * Het standaardtype van het viewpoint is Flow, en dat klopt tussen twee
   * stappen. Naar een procesresultaat niet: een Outcome is een
   * motivatie-element, en de officiele tabellen (C260 B.5) kennen daar geen
   * Flow of Triggering naartoe — wel een Realization, want de stap brengt de
   * uitkomst tot stand. Zonder deze keuze werd de relatie door de matrix
   * geweigerd en gebeurde er bij het loslaten zichtbaar niets (gemeld
   * 2026-09-17: "het lukt me niet om een stroom te tekenen naar het process
   * result").
   *
   * De volgorde is de voorkeursvolgorde; alle drie worden door dit canvas als
   * stroom getekend. Kan geen van drieen, dan zegt de melding dat.
   */
  const stroomTypeVoor = useCallback(
    (bron: Concept, doel: Concept): string | null => {
      const standaard =
        getRegistry().getViewKind(diagram.viewKind)?.default_relation_type ?? "archimate.Flow";
      const mag = new Set(validRelationTypesBetween(bron.typeId, doel.typeId));
      for (const kandidaat of [standaard, "archimate.Triggering", "archimate.Realization"]) {
        if (mag.has(kandidaat)) return kandidaat;
      }
      return null;
    },
    [diagram.viewKind],
  );

  /**
   * De vraag hoe twee stromen samenkomen of een stap splitst (regel A).
   * Bewaart de SCHERM-positie; het menu rekent die bij het tekenen om naar
   * het buitenste vlak, zodat het niet meekrimpt met de zoom.
   */
  /** De XOR-vertakkingen op dit diagram: een stap ervoor hoort te beslissen. */
  const xorIds = useMemo(
    () =>
      new Set(
        [...model.kaarten.values()]
          .filter((k) => (k.concept.stereotypes ?? []).includes(XOR_STEREOTYPE))
          .map((k) => k.concept.id),
      ),
    [model],
  );

  const [vertakkingVraag, setVertakkingVraag] = useState<
    (Omit<VertakkingVraag, "x" | "y"> & { clientX: number; clientY: number }) | null
  >(null);

  const sleepStroom = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>, sleutel: string) => {
      event.stopPropagation();
      event.preventDefault();
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const doel = event.currentTarget;
      const vlak = doel.parentElement;
      if (!vlak) return;
      doel.setPointerCapture(event.pointerId);
      const naarCanvas = (e: { clientX: number; clientY: number }) => {
        const r = vlak.getBoundingClientRect();
        const schaal = r.width === 0 ? 1 : vlak.clientWidth / r.width;
        return { x: (e.clientX - r.left) * schaal, y: (e.clientY - r.top) * schaal };
      };
      setTrekt({ van: sleutel, naar: naarCanvas(event) });
      const beweeg = (e: PointerEvent) => {
        setTrekt({ van: sleutel, naar: naarCanvas(e) });
      };
      const stop = (e: PointerEvent) => {
        doel.removeEventListener("pointermove", beweeg);
        doel.removeEventListener("pointerup", stop);
        doel.removeEventListener("pointercancel", stop);
        setTrekt(null);
        // Welke kaart ligt onder de muis? Uitgerekend uit het model en niet
        // uit de DOM: het rondje zelf vangt de gebeurtenis, en een hit-test op
        // het scherm faalt zodra er iets overheen ligt.
        const punt = naarCanvas(e);
        let doelKaart: { concept: Concept } | null = null;
        for (const kaart of model.kaarten.values()) {
          if (kaart.sleutel === sleutel) continue;
          if (
            punt.x >= kaart.x &&
            punt.x <= kaart.x + kaart.breedte &&
            punt.y >= kaart.y &&
            punt.y <= kaart.y + kaart.hoogte
          ) {
            doelKaart = kaart;
            break;
          }
        }
        const bron = model.kaarten.get(sleutel);
        if (!doelKaart || !bron || doelKaart.concept.id === bron.concept.id) return;
        const typeId = stroomTypeVoor(bron.concept, doelKaart.concept);
        if (!typeId) {
          useWorkspaceData.setState({ lastError: t("swimlane.noFlowBetween") });
          return;
        }
        /**
         * Regel A: hoogstens één stroom een stap in, en één eruit — behalve
         * de takken van een beslisstap. Breekt deze stroom dat, dan vraagt
         * het canvas wat het samenkomen of splitsen betekent en zet het de
         * vertakking er zelf tussen (zie `voegVertakkingIn`).
         *
         * Loopt de bestaande stroom al via een vertakking, dan sluit de
         * nieuwe daar gewoon op aan: dan is de betekenis al gekozen.
         */
        const getekend = model.stromen.map((st) => st.relation);
        const inDoel = isStap(doelKaart.concept)
          ? getekend.filter((r) => r.targetConceptId === doelKaart.concept.id)
          : [];
        const uitBron =
          isStap(bron.concept) && !isBeslisstap(bron.concept)
            ? getekend.filter((r) => r.sourceConceptId === bron.concept.id)
            : [];
        const alsVertakking = (id: string) => {
          const c = useWorkspaceData.getState().store.getConcept(id);
          return c && isVertakking(c) ? c : null;
        };
        const ws = useWorkspaceData.getState();
        if (inDoel.length > 0 && uitBron.length > 0) {
          useWorkspaceData.setState({ lastError: t("swimlane.flowRule.both") });
          return;
        }
        if (inDoel.length > 0) {
          const al = inDoel[0] ? alsVertakking(inDoel[0].sourceConceptId) : null;
          if (al) {
            void ws
              .connectConcepts(
                workspaceId,
                branchId,
                bron.concept.id,
                al.id,
                inDoel[0]?.typeId ?? typeId,
              )
              .then(() => markeerOverdrachten(bron.concept.id));
            return;
          }
          setVertakkingVraag({
            clientX: e.clientX,
            clientY: e.clientY,
            richting: "samen",
            van: bron.concept.id,
            naar: doelKaart.concept.id,
            bestaand: inDoel[0]?.id ?? "",
          });
          return;
        }
        if (uitBron.length > 0) {
          const al = uitBron[0] ? alsVertakking(uitBron[0].targetConceptId) : null;
          if (al) {
            void ws
              .connectConcepts(
                workspaceId,
                branchId,
                al.id,
                doelKaart.concept.id,
                uitBron[0]?.typeId ?? typeId,
              )
              .then(() => markeerOverdrachten(bron.concept.id));
            return;
          }
          setVertakkingVraag({
            clientX: e.clientX,
            clientY: e.clientY,
            richting: "splits",
            van: bron.concept.id,
            naar: doelKaart.concept.id,
            bestaand: uitBron[0]?.id ?? "",
          });
          return;
        }
        void ws
          .connectConcepts(workspaceId, branchId, bron.concept.id, doelKaart.concept.id, typeId)
          .then(() => markeerOverdrachten(bron.concept.id));
      };
      doel.addEventListener("pointermove", beweeg);
      doel.addEventListener("pointerup", stop);
      doel.addEventListener("pointercancel", stop);
    },
    [workspaceId, branchId, stroomTypeVoor, model, markeerOverdrachten, t],
  );

  /**
   * De hoogte van een baan slepen. Staat op de bewoner, dus per diagram, en
   * heeft een ondergrens: lager dan een kaart heeft geen zin.
   */
  const sleepBaanHoogte = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, bewonerId: string, begin: number) => {
      event.stopPropagation();
      event.preventDefault();
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const doel = event.currentTarget;
      doel.setPointerCapture(event.pointerId);
      const startY = event.clientY;
      let laatste = begin;
      const beweeg = (e: PointerEvent) => {
        laatste = Math.round(Math.max(70, Math.min(600, begin + (e.clientY - startY))));
        setBaanMeet({ bewonerId, hoogte: laatste });
      };
      const stop = () => {
        doel.removeEventListener("pointermove", beweeg);
        doel.removeEventListener("pointerup", stop);
        doel.removeEventListener("pointercancel", stop);
        setBaanMeet(null);
        // De kaarten schuiven mee doordat ze aan hun BAAN hangen
        // (`swimlaneBaanId`), niet doordat we hier posities herschrijven. Dat
        // deed een eerdere poging wél, en dan verhuist ook van alles waar de
        // modelleur niet aan zat.
        useWorkspaceData
          .getState()
          .setSwimlaneLaneHeightOnCanvas(workspaceId, branchId, bewonerId, laatste);
      };
      doel.addEventListener("pointermove", beweeg);
      doel.addEventListener("pointerup", stop);
      doel.addEventListener("pointercancel", stop);
    },
    [workspaceId, branchId],
  );

  /**
   * De maat van een kaart slepen.
   *
   * Sharp tekent niet alles even groot: een beslisstap is klein, een stap met
   * een lange naam breed. De breedte blijft binnen de kolomafstand en de
   * hoogte binnen de baan — anders schuift een kaart over z'n buren heen en
   * is niet meer te zien in welke baan of kolom 'ie hoort.
   */
  /** Het rechtermuismenu op een stroom — hetzelfde menu als op het gewone
   *  canvas (lijnsoort, richting, knikpunten). */
  const [relatieMenu, setRelatieMenu] = useState<RelatieMenuStand | null>(null);
  const [objectMenu, setObjectMenu] = useState<ConceptMenuStand | null>(null);
  const [canvasMenu, setCanvasMenu] = useState<CanvasMenuStand | null>(null);
  /** De zoom-opdrachten van `ZoomPan`, voor het canvas-menu. */
  const zoomActies = useRef<ZoomPanActies | null>(null);
  /**
   * Het buitenste vlak. De menu's hangen hierin en niet in de tekening zelf:
   * die staat in de zoom-laag, dus een menu daarin zou meekrimpen met de zoom
   * en op 75% als een miniatuur verschijnen.
   */
  const buitenRef = useRef<HTMLDivElement | null>(null);
  /**
   * Wanneer een kaart, baankop of stroom z'n eigen menu opende.
   *
   * Een ref en geen state: `ZoomPan` opent het canvas-menu bij muis-omhoog,
   * in dezelfde gebeurtenis-reeks als de rechtsklik, dus een state-check ziet
   * daar nog de oude waarde en je zou twee menu's naast elkaar krijgen.
   */
  const objectMenuNet = useRef(0);
  /** Muispositie ten opzichte van dat vlak — daar komt het menu te staan. */
  const menuPlek = (e: { clientX: number; clientY: number }) => {
    const r = buitenRef.current?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  };

  /**
   * Rechtsklik op een object: hetzelfde menu als op het gewone canvas.
   *
   * Zonder deze handler krijg je dat van de browser — "Opnieuw laden",
   * "Afdrukken" — en dat is geen menu dat over je model gaat. Net als daar
   * wijst een rechtsklik het object eerst aan, zodat het menu gaat over wat je
   * ziet oplichten.
   */
  const openObjectMenu = useCallback(
    (e: ReactMouseEvent, conceptId: string, sleutel?: string) => {
      if (useSession.getState().readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      if (sleutel)
        kiesKaart(sleutel, conceptId, { metaKey: false, ctrlKey: false, shiftKey: false });
      else selectConcept(conceptId);
      objectMenuNet.current = Date.now();
      setCanvasMenu(null);
      setObjectMenu({ ...menuPlek(e), conceptId });
    },
    // kiesKaart en selectConcept zijn stabiel genoeg voor deze afhandeling
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** De baanhoogte tijdens het slepen. */
  const [baanMeet, setBaanMeet] = useState<{ bewonerId: string; hoogte: number } | null>(null);

  /** De maat die tijdens het slepen te zien is; `null` = wat er opgeslagen staat. */
  const [meet, setMeet] = useState<{ sleutel: string; breedte: number; hoogte: number } | null>(
    null,
  );

  const sleepMaat = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>, stapId: string, begin: Maat) => {
      event.stopPropagation();
      event.preventDefault();
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      const doel = event.currentTarget;
      doel.setPointerCapture(event.pointerId);
      const startX = event.clientX;
      const startY = event.clientY;
      let laatste: Maat = begin;
      const beweeg = (e: PointerEvent) => {
        laatste = {
          breedte: Math.round(
            Math.max(90, Math.min(KOLOM_AFSTAND - 20, begin.breedte + (e.clientX - startX))),
          ),
          hoogte: Math.round(
            Math.max(36, Math.min(BAAN_HOOGTE - 16, begin.hoogte + (e.clientY - startY))),
          ),
        };
        // De maat gaat via de state naar de kaart, niet via het DOM-element
        // ernaast: sinds de grepen achter álle kaarten staan is de buurman
        // van deze greep het ronde verbindingsknopje, en dat rekte mee uit
        // tot een ellips.
        setMeet({ sleutel: stapId, ...laatste });
      };
      const stop = () => {
        doel.removeEventListener("pointermove", beweeg);
        doel.removeEventListener("pointerup", stop);
        doel.removeEventListener("pointercancel", stop);
        setMeet(null);
        useWorkspaceData.getState().resizeNodeOnCanvas(workspaceId, branchId, stapId, {
          width: laatste.breedte,
          height: laatste.hoogte,
        });
      };
      doel.addEventListener("pointermove", beweeg);
      doel.addEventListener("pointerup", stop);
      doel.addEventListener("pointercancel", stop);
    },
    [workspaceId, branchId],
  );

  /**
   * Hoeveel regels naam er in een kaart passen. De ondertitel (het resultaat
   * of het beslis-criterium) neemt er één af.
   */
  const regelsInKaart = (hoogte: number, stap: Concept): number => {
    const regelhoogte = 17;
    const voorOndertitel = ondertitel(stap) ? regelhoogte : 0;
    return Math.max(1, Math.floor((hoogte - 12 - voorOndertitel) / regelhoogte));
  };

  const naam = (c?: Concept): string =>
    c ? resolveBilingual(c.name, lang) || t("swimlane.unnamed") : t("swimlane.unnamed");

  /**
   * Wat er OP de kaart staat.
   *
   * Leeg zolang het object de naam van z'n palet-tegel draagt: dan is 'ie nog
   * niet benoemd, en Sharp tekent 'm juist als lege doos om eerst de stroom
   * vast te leggen (H8, fig. 8.4). De tooltip en het attributenpaneel tonen
   * wél wat het is, dus je raakt niets kwijt.
   */
  const kaartNaam = (c: Concept): string =>
    heeftStandaardnaam(c) ? "" : resolveBilingual(c.name, lang);

  /** Wat de tooltip op een stroom vertelt: waar 'ie heen gaat, welk
   *  relatietype eronder ligt, en of 't een overdracht is.
   *
   *  Het type staat erbij sinds alle stromen er hetzelfde uitzien: de lijn
   *  verraadt 'm niet meer, en dan hoort 'ie ergens terug te lezen te zijn
   *  zonder het attributenpaneel te openen. De eindpunten komen uit de
   *  kaarten en niet uit de stappen — een procesresultaat is geen stap, en
   *  gaf hier dus "(zonder naam)". */
  /**
   * Het selectiekader: Shift + slepen op een lege plek.
   *
   * Slepen op een lege plek trekt een kader om een groep objecten — met of
   * zonder Shift. Het React-Flow-canvas staat op dezelfde regel
   * (`selectionOnDrag`), want een kader met een verborgen toets erbij vindt
   * niemand: gemeld als "de lasso is afwezig" terwijl 'ie met Shift wél
   * werkte. Pannen doe je met rechts, midden of spatie, dus de linkerknop is
   * vrij. Alles wat het kader RAAKT telt mee; dat is vergevingsgezinder dan
   * "volledig omsluiten" en het is wat React Flow ook doet.
   */
  const [kader, setKader] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(
    null,
  );
  const startKader = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    // Op een kaart, een greep of een knop betekent slepen iets anders —
    // verplaatsen, verschalen, een stroom trekken. Alleen op een LEGE plek is
    // slepen een selectie.
    if (event.target instanceof Element && event.target.closest("[data-stap-id], button")) {
      return;
    }
    const vlak = event.currentTarget;
    const rect = vlak.getBoundingClientRect();
    const schaal = rect.width === 0 ? 1 : vlak.clientWidth / rect.width;
    const naarCanvas = (e: { clientX: number; clientY: number }) => ({
      x: (e.clientX - rect.left) * schaal,
      y: (e.clientY - rect.top) * schaal,
    });
    const begin = naarCanvas(event);
    // GEEN preventDefault/stopPropagation hier. `preventDefault` op een
    // pointerdown onderdrukt de muis-gebeurtenissen die de browser erna
    // genereert — dus ook de mousedown waarop elk menu z'n "klik buiten"
    // herkent, en dan blijft een contextmenu openstaan bij een klik op het
    // canvas (gemeld 2026-09-19). Het gebeurt pas bij de eerste BEWEGING,
    // waar het hoort: dan begint er een sleep en wil je geen tekstselectie.
    // Geen pointer capture: zonder `preventDefault` op de pointerdown kan de
    // browser 'm onderweg weer afpakken, en dan komt de pointerup nooit bij
    // het vlak aan — het kader blijft dan staan na loslaten. `window` mist
    // 'm nooit.
    let laatste = { ...begin };
    setKader({ x1: begin.x, y1: begin.y, x2: begin.x, y2: begin.y });
    const beweeg = (e: PointerEvent | MouseEvent) => {
      e.preventDefault();
      laatste = naarCanvas(e);
      setKader({ x1: begin.x, y1: begin.y, x2: laatste.x, y2: laatste.y });
    };
    const stop = () => {
      window.removeEventListener("pointermove", beweeg);
      window.removeEventListener("mousemove", beweeg);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("blur", stop);
      setKader(null);
      const links = Math.min(begin.x, laatste.x);
      const rechts = Math.max(begin.x, laatste.x);
      const boven = Math.min(begin.y, laatste.y);
      const onder = Math.max(begin.y, laatste.y);
      if (rechts - links < 4 && onder - boven < 4) return;
      const geraakt = [...modelRef.current.kaarten.values()]
        .filter(
          (k) => k.x < rechts && k.x + k.breedte > links && k.y < onder && k.y + k.hoogte > boven,
        )
        .map((k) => k.sleutel);
      setSelectie(geraakt);
      const eerste = geraakt[0];
      const kaart = eerste ? modelRef.current.kaarten.get(eerste) : undefined;
      if (kaart) selectInstance(kaart.sleutel, kaart.concept.id);
      // Een sleepselectie kent geen klikvolgorde, dus ook geen maatstaf voor
      // gelijke maat en uitlijnen — dezelfde afspraak als op het gewone
      // canvas.
      useUi.getState().setSelectionAnchorConceptId(null);
    };
    window.addEventListener("pointermove", beweeg);
    window.addEventListener("mousemove", beweeg);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    // Ook op de muis-variant: niet elke bron levert een pointerup (een
    // afgebroken sleep, een venster dat focus verliest), en een kader dat
    // blijft hangen is erger dan een luisteraar te veel.
    window.addEventListener("mouseup", stop);
    window.addEventListener("blur", stop);
  }, []);

  /**
   * Dezelfde toetsen als op het gewone canvas.
   *
   * Delete haalt het voorkomen van het diagram (het object blijft bestaan);
   * mét Cmd, Ctrl of Alt verwijdert 'ie het object uit het model, met
   * dezelfde bevestiging. De pijltjes verschuiven de selectie een punt, met
   * Shift tien. Zonder dit deed een banendiagram niets op het toetsenbord,
   * terwijl elk ander diagram erop reageert.
   */
  useEffect(() => {
    if (!workspaceId || !branchId) return;
    const inVeld = (doel: EventTarget | null): boolean => {
      if (!(doel instanceof HTMLElement)) return false;
      const tag = doel.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      return doel.isContentEditable || !!doel.closest(".cm-editor");
    };

    const opToets = (e: KeyboardEvent) => {
      if (inVeld(e.target) || hernoemtRef.current) return;
      if (useSession.getState().readOnly) return;
      const sleutels = gekozenSleutelsRef.current;
      const ws = useWorkspaceData.getState();

      if (e.key === "Delete" || e.key === "Backspace") {
        const relId = useUi.getState().selectedRelationId;
        if (sleutels.length === 0 && !relId) return;
        e.preventDefault();
        e.stopPropagation();
        const hard = e.metaKey || e.ctrlKey || e.altKey;
        const conceptIds = [
          ...new Set(
            sleutels
              .map((k) => modelRef.current.kaarten.get(k)?.concept.id)
              .filter((id): id is string => !!id),
          ),
        ];
        if (!hard) {
          ws.beginUserUndoBatch();
          try {
            for (const k of sleutels) ws.hideConceptOnDiagram(workspaceId, branchId, k);
          } finally {
            ws.endUserUndoBatch("swimlane.hide");
          }
          return;
        }
        void (async () => {
          const langKey: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
          const ok = await confirmHardDelete({
            workspaceId,
            branchId,
            conceptIds,
            lang: langKey,
            standaloneEdgeCount: relId && conceptIds.length === 0 ? 1 : 0,
            conceptName: (id) => ws.store.getConcept(id)?.name,
          });
          if (!ok) return;
          await ws.removeBatch(
            workspaceId,
            branchId,
            conceptIds,
            relId && conceptIds.length === 0 ? [relId] : [],
          );
        })();
        return;
      }

      if (!e.key.startsWith("Arrow")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (sleutels.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      const stap = e.shiftKey ? 10 : 1;
      const dx = e.key === "ArrowLeft" ? -stap : e.key === "ArrowRight" ? stap : 0;
      const dy = e.key === "ArrowUp" ? -stap : e.key === "ArrowDown" ? stap : 0;
      if (dx === 0 && dy === 0) return;
      ws.beginUserUndoBatch();
      try {
        for (const k of sleutels) {
          const kaart = modelRef.current.kaarten.get(k);
          if (!kaart) continue;
          // De pijltjes verhuizen nooit naar een andere baan — dat doe je met
          // Alt + slepen. Omhoog stopt bij de bovenrand, omlaag groeit de
          // baan mee, net als bij slepen.
          const m = modelRef.current;
          const rij = kaart.rij ?? m.rijVanKaart(kaart.y, kaart.hoogte);
          const binnen = rij >= 0 && rij < m.banen.length;
          const top = binnen ? (m.baanToppen[rij] ?? RASTER_TOP) : MARGE + TITEL_HOOGTE - 2;
          const nieuweY = Math.max(top + 2, kaart.y + dy);
          ws.moveConceptOnCanvas(workspaceId, branchId, k, {
            x: Math.max(MARGE + m.kopBreedte, kaart.x + dx),
            y: nieuweY,
          });
          if (binnen) {
            ws.setSwimlaneLaneOnCanvas(workspaceId, branchId, k, m.banen[rij]?.id ?? null);
            laatBaanMeegroeien(rij, nieuweY + kaart.hoogte);
          }
        }
      } finally {
        ws.endUserUndoBatch("swimlane.move");
      }
    };

    window.addEventListener("keydown", opToets, { capture: true });
    return () => window.removeEventListener("keydown", opToets, { capture: true });
  }, [workspaceId, branchId, laatBaanMeegroeien]);

  const naamVanStroom = (relation: Relation, overdracht: boolean): string => {
    const opKaart = (id: string): Concept | undefined => {
      for (const kaart of model.kaarten.values()) if (kaart.concept.id === id) return kaart.concept;
      return undefined;
    };
    const heen = `${naam(opKaart(relation.sourceConceptId))} \u2192 ${naam(
      opKaart(relation.targetConceptId),
    )}`;
    const type = getRegistry().getRelationType(relation.typeId);
    const typeNaam = type ? resolveBilingual(type.label, lang) : relation.typeId;
    const regels = [`${heen} (${typeNaam})`];
    if (overdracht) regels.push(t("swimlane.legendHandoff"));
    return regels.join(" \u2014 ");
  };

  /**
   * Welke vertakking is dit, of geen?
   *
   * Sharp kent er drie (H8): precies één pad (XOR), alle paden (AND), één of
   * meer paden (OR). ArchiMate heeft er twee — een AND- en een OR-junction —
   * en maakt binnen die laatste geen verschil tussen "precies één" en "één of
   * meer". Het stereotype draagt dat onderscheid; zonder stereotype valt een
   * kale junction terug op wat z'n type zegt.
   */
  const vertakkingSoort = (c: Concept): "xor" | "and" | "of" | "samen" | null => {
    const st = c.stereotypes ?? [];
    if (st.includes("sharp.MergePoint")) return "samen";
    if (st.includes("sharp.ExclusiveBranch")) return "xor";
    if (st.includes("sharp.ParallelBranch")) return "and";
    if (st.includes("sharp.InclusiveBranch")) return "of";
    if (c.typeId === "archimate.AndJunction") return "and";
    if (c.typeId === "archimate.OrJunction") return "xor";
    return null;
  };

  /**
   * De vulling van een kaart.
   *
   * Uit dezelfde bron als de tegel in het typepaneel — de notation van het
   * stereotype, via `colorForConcept`. Dit canvas had z'n eigen twee tinten
   * geel, waardoor een stap op het canvas een andere kleur had dan de tegel
   * waar je 'm vandaan sleepte, en een essentiële stap (groen in het palet)
   * hier net zo geel was als een gewone. De RAND blijft de inkt van dit
   * canvas: dat is de lijnvoering, geen typering.
   */
  const vullingVan = (c: Concept): string =>
    colorForConcept({
      typeId: c.typeId,
      layer: getRegistry().getElementType(c.typeId)?.layer ?? "business",
      stereotypes: c.stereotypes ?? [],
      ...(c.attributes ? { attributes: c.attributes } : {}),
    }).bg;

  /**
   * De vulling van een SYMBOOL — de cirkel van een resultaat, de stopwatch
   * van een wachttijd.
   *
   * Die twee hebben geen vak dat je kunt inkleuren; hun kaart is doorzichtig
   * en alleen het teken staat er, dus de kleur van hun stereotype was nergens
   * te zien. Allebei krijgen ze nu de vulling van «Procesresultaat», zodat de
   * twee tekens als één familie lezen: een symbool op de lijn, geen vak in een
   * baan. De waarde komt uit de registry en niet uit een hex hier, anders
   * lopen het palet en het canvas weer uit elkaar.
   */
  const symboolVulling = (): string =>
    getRegistry().getStereotype("sharp.ProcessResult")?.notation?.fill ?? "#FFFFFF";

  /**
   * Welke vorm Sharp voor dit object gebruikt.
   *
   * Een procesresultaat is een cirkel met een zwarte stip — het doel waar de
   * stroom op uitkomt — met z'n naam ernaast. Een stap die het werk ophoudt
   * krijgt een stopwatch met de naam eronder. Al het andere is de gewone
   * kaart.
   */
  const vormVan = (c: Concept): "resultaat" | "wachttijd" | "vertakking" | "kaart" => {
    const st = c.stereotypes ?? [];
    if (vertakkingSoort(c)) return "vertakking";
    if (st.includes("sharp.ProcessResult") || c.typeId === "archimate.Outcome") return "resultaat";
    if (c.attributes?.step_contribution === "delays") return "wachttijd";
    return "kaart";
  };

  /** Beslisstap toont z'n criterium, gewone stap het resultaat dat 'ie oplevert. */
  const ondertitel = (stap: Concept): string => {
    const beslis = (stap.stereotypes ?? []).includes("sharp.DecisionStep");
    const veld = beslis ? stap.attributes?.decision_criteria : stap.attributes?.step_result;
    if (!veld || typeof veld !== "object") return "";
    return resolveBilingual(veld as { nl?: string; en?: string }, lang);
  };

  if (model.banen.every((b) => b.id === null && b.stapIds.length === 0)) {
    return (
      <div
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = sleepEffect(e.dataTransfer);
        }}
        onDrop={(e) => void opBaanGedropt(e, null)}
        className="flex h-full items-center justify-center p-8 text-center text-gray-500 text-sm"
      >
        {t("swimlane.noLanes")}
      </div>
    );
  }

  /**
   * Zet een vertakking tussen een bestaande stroom en de nieuwe (regel A).
   *
   * Bij SAMENKOMEN komt de vertakking vóór de stap: de bestaande stroom gaat
   * naar de vertakking, de nieuwe ook, en één stroom loopt door naar de stap.
   * Bij SPLITSEN andersom, ná de stap. De bestaande stroom wordt van eindpunt
   * gewisseld en NIET opnieuw aangemaakt, zodat z'n label ("ja", "nee") en
   * opmaak meegaan. Alle stromen aan de vertakking krijgen het type van de
   * bestaande: een junction verbindt relaties van hetzelfde type, anders
   * weigert de server.
   */
  const voegVertakkingIn = async (
    vraag: Omit<VertakkingVraag, "x" | "y">,
    soort: VertakkingSoort,
  ) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    const ws = useWorkspaceData.getState();
    const diagramId = ws.activeDiagramId;
    const bestaand = ws.store.getRelation(vraag.bestaand);
    if (!diagramId || !bestaand) return;
    const soortType = bestaand.typeId;
    const def = {
      xor: { typeId: "archimate.OrJunction", stereotype: "sharp.ExclusiveBranch" },
      and: { typeId: "archimate.AndJunction", stereotype: "sharp.ParallelBranch" },
      of: { typeId: "archimate.OrJunction", stereotype: "sharp.InclusiveBranch" },
    }[soort];
    // De naam van de palet-tegel, zodat het canvas 'm als "nog niet benoemd"
    // herkent en er geen label onder zet.
    const tegel = getRegistry()
      .listPaletteGroups()
      .flatMap((g) => g.entries ?? [])
      .find((en) => (en.default_stereotypes ?? [])[0] === def.stereotype);
    const naam = tegel?.label_override ?? { nl: soort.toUpperCase(), en: soort.toUpperCase() };
    const stap = vraag.richting === "samen" ? vraag.naar : vraag.van;
    const kaartSleutel = model.eersteVoorkomen.get(stap);
    const kaart = kaartSleutel ? model.kaarten.get(kaartSleutel) : undefined;
    if (!kaart) return;
    /**
     * Waar de vertakking komt: in het gat náást de stap — links bij
     * samenkomen, rechts bij splitsen — op de hoogte van z'n midden.
     *
     * Is dat gat te klein, dan schuift de kaart die in de weg staat net zoveel
     * op als nodig: bij samenkomen de stap zelf naar rechts, bij splitsen de
     * kaart(en) direct rechts van de stap. Anders landt de vertakking bovenop
     * een kaart en verdwijnen stip én label erachter. Het valt in dezelfde
     * ongedaan-stap; andere kaarten blijven staan.
     */
    const midY = kaart.y + kaart.hoogte / 2;
    const marge = 24;
    const nodig = VERTAKKING_MAAT + marge * 2;
    const inBand = (k: { y: number; hoogte: number }) =>
      k.y < midY + VERTAKKING_MAAT && k.y + k.hoogte > midY - VERTAKKING_MAAT;
    const anderen = [...model.kaarten.values()].filter(
      (k) => k.sleutel !== kaart.sleutel && inBand(k),
    );
    const verschuivingen: { sleutel: string; x: number; y: number }[] = [];
    let x: number;
    if (vraag.richting === "samen") {
      const vrijVan = Math.max(
        MARGE + model.kopBreedte,
        // Elke kaart die links van de stap begint telt, ook een die er half
        // overheen ligt: dan is het gat negatief en schuift de stap verder op.
        ...anderen.filter((k) => k.x < kaart.x).map((k) => k.x + k.breedte),
      );
      const gat = kaart.x - vrijVan;
      if (gat >= nodig) {
        x = Math.round(vrijVan + (gat - VERTAKKING_MAAT) / 2);
      } else {
        x = Math.round(vrijVan + marge);
        verschuivingen.push({ sleutel: kaart.sleutel, x: kaart.x + (nodig - gat), y: kaart.y });
      }
    } else {
      const rechts = kaart.x + kaart.breedte;
      // Alles wat de vrije plek raakt, ook een kaart die al half over de stap ligt.
      const inDeWeg = anderen.filter(
        (k) => k.x + k.breedte > rechts && k.x < rechts + nodig && k.x > kaart.x,
      );
      x = Math.round(rechts + marge);
      for (const k of inDeWeg) {
        verschuivingen.push({ sleutel: k.sleutel, x: rechts + nodig, y: k.y });
      }
    }
    const plek = { x, y: Math.round(midY - VERTAKKING_MAAT / 2) };
    ws.beginUserUndoBatch();
    try {
      for (const v of verschuivingen) {
        ws.moveConceptOnCanvas(workspaceId, branchId, v.sleutel, { x: Math.round(v.x), y: v.y });
      }
      const punt = await ws.addConceptOnCanvas(
        workspaceId,
        branchId,
        def.typeId,
        { ...(naam.nl ? { nl: naam.nl } : {}), ...(naam.en ? { en: naam.en } : {}) },
        plek,
        undefined,
        [def.stereotype],
      );
      if (!punt) return;
      ws.resizeNodeOnCanvas(workspaceId, branchId, punt.id, {
        width: VERTAKKING_MAAT,
        height: VERTAKKING_MAAT,
      });
      legBaanVast(punt.id, plek.y, VERTAKKING_MAAT);

      // De bestaande stroom van eindpunt wisselen, en een vastgepind
      // voorkomen aan die kant losmaken — anders tekent 'ie nog naar de stap.
      const pinVeld = vraag.richting === "samen" ? "targetInstanceId" : "sourceInstanceId";
      await ws.patchRelation(workspaceId, branchId, bestaand.id, {
        ...(vraag.richting === "samen"
          ? { target_concept_id: punt.id }
          : { source_concept_id: punt.id }),
        change_reason: "flow-rule-branch",
      });
      const vs = ws.store.getDiagram(diagramId)?.viewState ?? {};
      for (const [sleutel, ve] of Object.entries(vs)) {
        if ((ve.ref ?? sleutel) !== bestaand.id || !ve[pinVeld]) continue;
        const { [pinVeld]: _weg, ...rest } = ve;
        ws.applyViewElementOnCanvas(workspaceId, branchId, diagramId, sleutel, rest);
      }

      // Een stap met een XOR erachter beslist (H8 p228): dan hoort er
      // «Beslisstap» op in plaats van «Werkstap».
      const bronStap = ws.store.getConcept(vraag.van);
      if (
        vraag.richting === "splits" &&
        soort === "xor" &&
        bronStap &&
        isStap(bronStap) &&
        !isBeslisstap(bronStap)
      ) {
        await ws.patchConcept(workspaceId, branchId, bronStap.id, {
          stereotypes: alsBeslisstap(bronStap.stereotypes ?? []),
        });
      }
      // De nieuwe stroom loopt in beide richtingen via de vertakking.
      await ws.connectConcepts(workspaceId, branchId, vraag.van, punt.id, soortType);
      await ws.connectConcepts(workspaceId, branchId, punt.id, vraag.naar, soortType);
      await markeerOverdrachten(vraag.van);
    } finally {
      ws.endUserUndoBatch("swimlane.branch");
    }
  };

  return (
    <div className="relative flex h-full flex-col" ref={buitenRef}>
      {/* Dezelfde werkbalk als op het gewone canvas. Wat hier niet kan —
          uitlijnen en verdelen, want de plaatsing volgt uit het model —
          staat er uitgeschakeld bij in plaats van te verdwijnen. */}
      <RibbonPortal>
        <CanvasToolbar
          workspaceId={workspaceId}
          branchId={branchId}
          nodes={werkbalkNodes}
          selectedRelationId={selectedRelationId}
          selectedRelationIds={selectedRelationId ? [selectedRelationId] : []}
          selectedEdgeKeys={selectedRelationId ? [selectedRelationId] : []}
          nietBeschikbaar={{
            // Wat dit canvas niet doet: kaarten overlappen niet (geen
            // z-volgorde), de stromen worden hier zelf getekend (geen lijnstijl
            // of routing), er is geen type-icoon in de hoek, en een baankop
            // heeft een vaste maat. Uitlijnen vervalt niet meer — de plaatsing
            // is sinds de omzetting gewoon opgeslagen.
            zOrder: true,
            lijnen: true,
            overlay: true,
            iconen: true,
            maat: gekozenSleutel === null,
          }}
        />
      </RibbonPortal>
      <div className="flex flex-wrap items-center gap-4 border-gray-200 border-b bg-white px-4 py-1.5 text-[11px] text-gray-600">
        <button
          type="button"
          onClick={schikStappen}
          hidden={readOnly}
          disabled={model.kaarten.size === 0}
          className="rounded border border-gray-300 bg-white px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          title={t("swimlane.arrangeHint")}
        >
          {t("swimlane.arrange")}
        </button>
        <span>{t("swimlane.steps", { count: model.stappen.size })}</span>
        <span>{t("swimlane.handoffs", { count: model.aantalOverdrachten })}</span>
        <span className="flex items-center gap-1.5">
          <svg width="26" height="6" aria-hidden="true">
            <title>{t("swimlane.svgTitle.handoff")}</title>
            <line
              x1="0"
              y1="3"
              x2="26"
              y2="3"
              stroke="#6B7280"
              strokeWidth="1.4"
              strokeDasharray="6 4"
            />
          </svg>
          {t("swimlane.legendFlow")}
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="26" height="6" aria-hidden="true">
            <title>{t("swimlane.svgTitle.sequence")}</title>
            <line x1="0" y1="3" x2="26" y2="3" stroke="#6B7280" strokeWidth="1.6" />
          </svg>
          {t("swimlane.legendTriggering")}
        </span>
      </div>
      <ZoomPan
        className="flex-1"
        persistKey={`swimlane:${diagram.id}`}
        onActies={(a) => {
          zoomActies.current = a;
        }}
        onContextMenu={(e) => {
          // Een kaart, een baankop en een stroom vangen hun eigen rechtsklik
          // af; wat hier aankomt is dus het canvas zelf.
          if (Date.now() - objectMenuNet.current < 500) return;
          if (useSession.getState().readOnly) return;
          setObjectMenu(null);
          setRelatieMenu(null);
          setCanvasMenu(menuPlek(e));
        }}
      >
        <div
          // `select-none`: op een tekenvlak wil je geen tekstselectie. Het
          // stond er niet, en zonder de `preventDefault` op pointerdown — die
          // eruit moest omdat 'ie de mousedown onderdrukte waarop de menu's
          // sluiten — ging de browser tijdens het slepen van een selectiekader
          // de labels lopen markeren. Het invoerveld bij hernoemen zet 'm weer
          // aan; daar wil je juist wél kunnen selecteren.
          className="relative select-none"
          data-swimlane-vlak=""
          style={{ width: model.breedte, height: model.hoogte }}
          // Loslaten buiten de banen hoort ook te werken: een uitvoerder
          // krijgt daar z'n eigen baan (anders kun je er geen tweede
          // bijzetten zodra de bestaande banen het vlak niet vullen), en een
          // stap belandt bij het werk dat nog geen uitvoerder heeft.
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = sleepEffect(e.dataTransfer);
          }}
          onDrop={(e) => void opBaanGedropt(e, null, { baanRelatief: false })}
          onPointerDown={startKader}
          // Klikken op een lege plek laat los wat er geselecteerd was —
          // `onPaneClick` op het gewone canvas doet hetzelfde. Alles wat wél
          // iets selecteert houdt z'n klik bij zich (stopPropagation).
          onClick={() => {
            setSelectie([]);
            useUi.getState().selectConcept(null);
            useUi.getState().selectRelation(null);
          }}
        >
          {/* De titelbalk, zoals Sharp 'm tekent: de naam van het diagram in
              een kader boven de banen (Fig 8.1, p204). Dubbelklik hernoemt
              het diagram — dezelfde weg als op de tab. */}
          <div
            className="absolute flex items-center border border-gray-500 bg-white px-3 font-medium text-gray-800 text-sm"
            style={{
              left: MARGE,
              top: MARGE,
              width: model.breedte - MARGE * 2,
              height: TITEL_HOOGTE,
            }}
            title={t("swimlane.titleHint")}
            // Eén klik opent de eigenschappen van het diagram — daar staat het
            // tijdstip dat hier tussen blokhaken verschijnt. Zonder deze klik
            // was dat formulier alleen via het detailpaneel in de Explorer te
            // bereiken, en dat vindt niemand.
            onClick={(e) => {
              e.stopPropagation();
              const ui = useUi.getState();
              ui.selectDiagram(diagram.id);
              ui.setAttributePanelOpen(true);
            }}
            onDoubleClick={() => {
              const ws = useWorkspaceData.getState();
              if (ws.activeDiagramId) {
                window.dispatchEvent(
                  new CustomEvent("ae:rename-diagram", {
                    detail: { diagramId: ws.activeDiagramId },
                  }),
                );
              }
            }}
          >
            {resolveBilingual(diagram.name, lang) || t("swimlane.unnamed")}
            {/* Het tijdstip tussen blokhaken achter de naam, zoals Sharp het
                noteert ("Aanvraag verwerken [11:15]"). Leeg = niets. */}
            {typeof diagram.attributes?.time_point === "string" &&
            diagram.attributes.time_point.trim() ? (
              <span className="ml-1.5 font-normal text-gray-600">
                [{diagram.attributes.time_point.trim()}]
              </span>
            ) : null}
          </div>

          {/* De open strook boven de eerste baan — spiegelbeeld van die
              onderaan. Hier zet je een uitvoerder neer die bovenaan hoort. */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = sleepEffect(e.dataTransfer);
            }}
            onDrop={(e) => void opBaanGedropt(e, null, { bovenaan: true })}
            className="absolute flex items-center border-gray-300 border-b border-dashed bg-gray-50/40"
            style={{
              left: MARGE,
              top: MARGE + TITEL_HOOGTE,
              width: model.breedte - MARGE * 2,
              height: BAAN_HOOGTE,
            }}
          >
            <span className="px-3 text-[11px] text-gray-400" style={{ width: KOP_BREEDTE }}>
              {t("swimlane.newLaneZoneTop")}
            </span>
          </div>

          {/* banen */}
          {model.banen.map((baan, rij) => {
            const top = model.baanToppen[rij] ?? RASTER_TOP;
            const baanHoogte =
              baanMeet?.bewonerId === baan.id
                ? baanMeet.hoogte
                : (model.baanHoogtes[rij] ?? BAAN_HOOGTE);
            const naamloos = baan.id === null;
            const baanOpmaak = baan.id ? opmaakVan(baan.id) : {};
            const baanVulling = baanOpmaak.fillColor;
            return (
              <div
                key={baan.id ?? "__zonder"}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = sleepEffect(e.dataTransfer);
                }}
                onDrop={(e) => void opBaanGedropt(e, baan.id)}
                className={`absolute border-gray-500 border-t ${
                  baanVulling
                    ? ""
                    : naamloos
                      ? "bg-amber-50/60"
                      : rij % 2 === 0
                        ? "bg-white"
                        : "bg-gray-50/60"
                }`}
                style={{
                  left: MARGE,
                  top,
                  width: model.breedte - MARGE * 2,
                  height: baanHoogte,
                  ...(baanVulling ? { backgroundColor: baanVulling } : {}),
                }}
              >
                <div
                  draggable={baan.id !== null && !readOnly}
                  onDragStart={(e) => {
                    if (!baan.id) return;
                    e.dataTransfer.setData("application/ae-swimlane-lane", baan.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  // Klikken selecteert de bewoner, dubbelklikken opent 'm in
                  // het attributenpaneel — net als een object op het gewone
                  // canvas. Zonder deze twee is een baan niet te hernoemen:
                  // de kop is de enige plek waar de bewoner te zien is.
                  onClick={(e) => {
                    e.stopPropagation();
                    if (baan.id) selectConcept(baan.id);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (!baan.id) return;
                    selectConcept(baan.id);
                    useUi.getState().setAttributePanelOpen(true);
                  }}
                  onContextMenu={(e) => {
                    if (!baan.id) return;
                    openObjectMenu(e, baan.id);
                  }}
                  // Een uitvoerder op de baankop laten vallen wisselt de
                  // eigenaar. Al het andere — een stap, een verse uit het
                  // palet — laten we doorborrelen naar de baan zelf, die er
                  // z'n eigen meting op doet.
                  onDrop={(e) => {
                    // Een baan die op een andere baan valt: dat is de
                    // volgorde, niet de eigenaar.
                    const baanBron = e.dataTransfer.getData("application/ae-swimlane-lane");
                    if (baanBron && baan.id) {
                      e.preventDefault();
                      e.stopPropagation();
                      // De onderste helft van een kop betekent eronder, de
                      // bovenste erboven — zoals elke lijst die je herordent.
                      const r = e.currentTarget.getBoundingClientRect();
                      verplaatsBaan(baanBron, baan.id, e.clientY - r.top > r.height / 2);
                      return;
                    }
                    const bron = e.dataTransfer.getData("application/ae-existing-concept-id");
                    if (!bron || baan.id === null || bron === baan.id) return;
                    const gesleept = useWorkspaceData.getState().store.getConcept(bron);
                    // Alleen een uitvoerder wisselt de bewoner. Een resultaat
                    // of gebeurtenis borrelt door naar de baan en wordt daar
                    // een kaart — anders werd 'ie stil de bewoner en haalde de
                    // volgende wissel z'n voorkomen van het diagram.
                    if (!gesleept || !isBaanBewoner(gesleept)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    void wisselBaanEigenaar(baan.id, bron);
                  }}
                  title={naamloos ? undefined : t("swimlane.laneOwnerHint")}
                  // Een geselecteerde kop krijgt dezelfde blauwe ring als een
                  // geselecteerde kaart: zonder dat merkteken zie je niet dat
                  // je de bewoner te pakken hebt en dat de werkbalk op hém
                  // werkt.
                  className={`absolute inset-y-0 left-0 flex flex-col items-center justify-center border-gray-500 border-r ${
                    model.kopBreedte === KOP_BREEDTE_STAAND ? "px-1.5" : "px-3"
                  } ${naamloos ? "" : "cursor-pointer"} ${
                    baan.id && selectedConceptId === baan.id
                      ? "ring-2 ring-blue-400 ring-inset"
                      : ""
                  }`}
                  style={{ width: model.kopBreedte }}
                >
                  {baan.id && hernoemt === baan.id ? (
                    naamVeld(
                      baan.id,
                      baan.id,
                      "w-full rounded border border-blue-400 bg-white px-1 py-0.5 font-medium text-gray-900 text-sm focus:outline-none",
                    )
                  ) : (
                    <div
                      className="overflow-hidden font-medium text-gray-800 leading-tight"
                      lang={lang}
                      style={{
                        ...tekstStijl(baanOpmaak, DIAGRAM_BASE_FONT),
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                      title={naamloos ? undefined : naam(baan.bewoner)}
                      onDoubleClick={(e) => {
                        if (!baan.id) return;
                        // Dubbelklik op de naam hernoemt; op de rest van de
                        // kop opent 'ie het attributenpaneel.
                        e.stopPropagation();
                        beginHernoemen(baan.id, baan.id, naam(baan.bewoner));
                      }}
                    >
                      {naamloos ? t("swimlane.unassigned") : naam(baan.bewoner)}
                    </div>
                  )}
                  {naamloos ? (
                    <div className="text-[11px] text-gray-500 leading-tight">
                      {t("swimlane.unassignedHint")}
                    </div>
                  ) : baan.pool ? (
                    <div className="truncate text-[11px] text-gray-500">
                      {t("swimlane.lanePool", { name: naam(baan.pool) })}
                    </div>
                  ) : null}
                </div>

                {/* De onderrand van een baan is sleepbaar: hoger maken geeft
                    ruimte voor twee stappen onder elkaar — parallel werk bij
                    dezelfde uitvoerder. */}
                {baan.id && !readOnly ? (
                  // biome-ignore lint/a11y/noStaticElementInteractions: sleepgreep op een canvas-rand
                  <div
                    title={t("swimlane.laneHeightHint")}
                    onPointerDown={(e) => baan.id && sleepBaanHoogte(e, baan.id, baanHoogte)}
                    className="absolute right-0 bottom-0 left-0 h-1.5 cursor-ns-resize hover:bg-blue-300/60"
                  />
                ) : null}
              </div>
            );
          })}

          {relatieMenu ? (
            <RelationContextMenu
              stand={relatieMenu}
              workspaceId={workspaceId}
              branchId={branchId}
              onClose={() => setRelatieMenu(null)}
            />
          ) : null}

          {objectMenu ? (
            <ConceptContextMenu
              stand={objectMenu}
              workspaceId={workspaceId}
              branchId={branchId}
              onClose={() => setObjectMenu(null)}
            />
          ) : null}

          {/* De open strook onder de laatste baan. Altijd aanwezig, altijd
              hetzelfde: hier zet je een uitvoerder neer voor een nieuwe baan,
              of laat je een stap los om 'm van z'n uitvoerder af te halen. */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = sleepEffect(e.dataTransfer);
            }}
            onDrop={(e) => void opBaanGedropt(e, null)}
            className="absolute flex items-center border-gray-300 border-t border-dashed bg-gray-50/40"
            style={{
              left: MARGE,
              top: model.onderkantBanen,
              width: model.breedte - MARGE * 2,
              height: BAAN_HOOGTE,
            }}
          >
            <span className="px-3 text-[11px] text-gray-400" style={{ width: KOP_BREEDTE }}>
              {t("swimlane.newLaneZone")}
            </span>
          </div>

          {/* stromen */}
          <svg
            className="pointer-events-none absolute inset-0"
            width={model.breedte}
            height={model.hoogte}
            aria-hidden="true"
          >
            <title>{t("swimlane.svgTitle.flows")}</title>
            {/* De pijlpunten. Een marker erft de lijnkleur niet, dus er is er
                één per kleur die dit canvas gebruikt. */}
            <defs>
              {[
                ["grijs", INKT],
                ["blauw", INKT_GEKOZEN],
              ].map(([naam, kleurCode]) => (
                <marker
                  key={naam}
                  id={`swimlane-pijl-${naam}`}
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={kleurCode} />
                </marker>
              ))}
            </defs>
            {trekt
              ? (() => {
                  const k = model.kaarten.get(trekt.van);
                  if (!k) return null;
                  return (
                    <line
                      x1={k.x + k.breedte}
                      y1={k.y + k.hoogte / 2}
                      x2={trekt.naar.x}
                      y2={trekt.naar.y}
                      stroke="#2563EB"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                    />
                  );
                })()
              : null}
            {model.stromen.map(({ relation, overdracht }) => {
              // Welke voorkomens verbindt deze stroom? Wijst de relatie er zelf
              // twee aan, dan die; anders het eerste voorkomen per stap —
              // dezelfde regel als op het gewone canvas.
              const ve = diagram.viewState?.[relation.id];
              const vanSleutel =
                (ve?.sourceInstanceId && model.kaarten.has(ve.sourceInstanceId)
                  ? ve.sourceInstanceId
                  : null) ?? model.eersteVoorkomen.get(relation.sourceConceptId);
              const naarSleutel =
                (ve?.targetInstanceId && model.kaarten.has(ve.targetInstanceId)
                  ? ve.targetInstanceId
                  : null) ?? model.eersteVoorkomen.get(relation.targetConceptId);
              const van = vanSleutel ? model.kaarten.get(vanSleutel) : undefined;
              const naar = naarSleutel ? model.kaarten.get(naarSleutel) : undefined;
              if (!van || !naar) return null;
              const punten = model.aanhechting.get(relation.id);
              // Loopt deze stroom terug? Dan hangt 'ie boven de rij en niet
              // ertussen: uit de bovenkant van de bron, over de kaarten heen,
              // de bovenkant van het doel in. Dat is de vorm die een
              // terugkoppeling in Sharp's figuren heeft, en de enige die geen
              // kaarten doorkruist.
              const terug = naar.x + naar.breedte <= van.x;
              /**
               * Waar hecht een stroom aan deze kaart?
               *
               * Drie soorten, en het verschil zit 'm in wat je ziet staan.
               * Een STAP is een vlak: de lijn stopt op z'n rand. Een
               * VERTAKKING is een punt: de lijn loopt naar het hart en
               * verdwijnt onder de stip. Een RESULTAAT en een WACHTTIJD zijn
               * een symbool in een veel breder vak — de cirkel links, de
               * stopwatch in het midden — en daar moet de lijn naar het
               * SYMBOOL, niet naar de rand van het vak. Anders stopt 'ie
               * tientallen punten ervandaan, in het luchtledige.
               */
              const hechtPunt = (
                k: { x: number; y: number; breedte: number; hoogte: number; concept: Concept },
                kant: "uit" | "in",
              ) => {
                const middenY = k.y + k.hoogte / 2;
                if (isVertakking(k.concept)) {
                  return {
                    x: k.x + k.breedte / 2,
                    y: middenY,
                    top: { x: k.x + k.breedte / 2, y: middenY },
                  };
                }
                const soort = vormVan(k.concept);
                if (soort === "resultaat" || soort === "wachttijd") {
                  // De cirkel van een resultaat staat links in het vak; de
                  // stopwatch staat in het midden.
                  const links =
                    soort === "resultaat"
                      ? k.x + SYMBOOL_MARGE
                      : k.x + k.breedte / 2 - SYMBOOL_MAAT / 2;
                  return {
                    x: kant === "in" ? links : links + SYMBOOL_MAAT,
                    y: middenY,
                    top: { x: links + SYMBOOL_MAAT / 2, y: middenY - SYMBOOL_MAAT / 2 },
                  };
                }
                return {
                  x: kant === "in" ? k.x : k.x + k.breedte,
                  y: kant === "in" ? (punten?.in ?? middenY) : (punten?.uit ?? middenY),
                  top: { x: k.x + k.breedte / 2, y: k.y },
                };
              };
              const bron = hechtPunt(van, "uit");
              const doel = hechtPunt(naar, "in");
              const start =
                terug && !isVertakking(van.concept) ? bron.top : { x: bron.x, y: bron.y };
              const eind =
                terug && !isVertakking(naar.concept) ? doel.top : { x: doel.x, y: doel.y };
              const knik = model.knikken.get(relation.id);
              const stuur = knikStuurpunt(start, eind, knik);
              const gekozen = selectedRelationId === relation.id;
              const pad = stroomPad(ve?.routing, start, eind, stuur, terug);
              // Een naamloze stroom hoort naamloos te zijn: `resolveBilingual`
              // geeft "(unnamed)" terug als er niets staat, en dat is een
              // hulpje voor objecten — op een lijn is het ruis.
              const heeftLabel = Object.values(relation.label ?? {}).some(
                (v) => typeof v === "string" && v.trim().length > 0,
              );
              const labelTekst = heeftLabel ? resolveBilingual(relation.label, lang) : "";
              const ruweVertraging = relation.attributes?.delay_qualitative;
              const vertraging =
                typeof ruweVertraging === "string" && ruweVertraging !== "none"
                  ? ruweVertraging
                  : null;
              // Zonder eigen routing ligt het label op het stuurpunt (dat is
              // waar de boog doorheen loopt); anders halverwege de eindpunten.
              const labelPunt =
                ve?.routing === "manual"
                  ? { x: stuur.x, y: stuur.y - 8 }
                  : terug
                    ? // Boven de omweg, want tussen de twee eindpunten in
                      // staan de kaarten waar de lijn omheen gaat.
                      { x: (start.x + eind.x) / 2, y: Math.min(start.y, eind.y) - 28 }
                    : { x: (start.x + eind.x) / 2, y: (start.y + eind.y) / 2 - 8 };
              // Eén kleur voor alle stromen. Dat een stroom een overdracht is
              // zie je doordat 'ie een baangrens kruist — Sharp kleurt 'm niet
              // apart, en twee kleuren door elkaar maakt het beeld onrustig
              // zonder iets toe te voegen. Blauw blijft de aangewezen lijn.
              const kleur = gekozen ? INKT_GEKOZEN : INKT;
              return (
                <g key={relation.id}>
                  <title>{naamVanStroom(relation, overdracht)}</title>
                  {/* Brede onzichtbare baan eronder: een lijn van anderhalve
                      punt is met de muis niet te raken. */}
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: SVG-pad in een canvas; selectie loopt via de kaarten en dit pad */}
                  <path
                    d={pad}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={14}
                    className="pointer-events-auto cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectRelation(relation.id);
                    }}
                    // Dubbelklik opent het attributenpaneel. Op dit canvas is
                    // de stroom de enige weg naar de instantie-attributen van
                    // een overdracht — er is geen tweede plek waar 'ie te
                    // pakken is.
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      selectRelation(relation.id);
                      useUi.getState().setAttributePanelOpen(true);
                    }}
                    onContextMenu={(e) => {
                      if (useSession.getState().readOnly) return;
                      e.preventDefault();
                      e.stopPropagation();
                      selectRelation(relation.id);
                      const vlak = e.currentTarget.closest("div");
                      const r = vlak?.getBoundingClientRect();
                      objectMenuNet.current = Date.now();
                      setCanvasMenu(null);
                      setRelatieMenu({
                        x: e.clientX - (r?.left ?? 0),
                        y: e.clientY - (r?.top ?? 0),
                        edgeId: relation.id,
                        relationId: relation.id,
                      });
                    }}
                  />
                  {/* Eén tekenwijze voor élke stroom: een dichte lijn met
                      een gevulde punt aan het eind. Op een banendiagram zegt
                      de lijn "hierna gebeurt dit" — welk ArchiMate-type dat
                      onderwater draagt (Flow tussen stappen, Realization naar
                      een resultaat) is een modelleer-detail, geen notatie.
                      Sharp tekent ze in hoofdstuk 8 dan ook allemaal gelijk;
                      streepjes en open punten door elkaar suggereren een
                      onderscheid dat de lezer niet kan duiden. Het relatietype
                      staat in de tooltip en in het attributenpaneel. */}
                  <path
                    d={pad}
                    fill="none"
                    stroke={kleur}
                    strokeWidth={gekozen ? 2.5 : 1.5}
                    markerEnd={`url(#swimlane-pijl-${gekozen ? "blauw" : "grijs"})`}
                    className="pointer-events-none"
                  />
                  {/* Het label op de lijn — de "ja" en "nee" bij een
                      beslisstap, of wat de modelleur er verder op zet. Stond
                      hier niet, terwijl het attributenpaneel 'm wel liet
                      invullen. Opmaak komt uit dezelfde velden als op het
                      gewone canvas. */}
                  {labelTekst ? (
                    <text
                      x={labelPunt.x + (ve?.labelOffset?.x ?? 0)}
                      y={labelPunt.y + (ve?.labelOffset?.y ?? 0)}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="pointer-events-none select-none"
                      style={{
                        fontSize: ve?.labelFontSize ?? 11,
                        fontWeight: ve?.labelBold ? 700 : 500,
                        ...(ve?.labelItalic ? { fontStyle: "italic" } : {}),
                        ...(ve?.labelFontFamily ? { fontFamily: ve.labelFontFamily } : {}),
                        fill: ve?.labelTextColor ?? kleur,
                        paintOrder: "stroke",
                        stroke: ve?.labelBgColor ?? "#FFFFFF",
                        strokeWidth: 4,
                        strokeLinejoin: "round",
                      }}
                    >
                      {labelTekst}
                    </text>
                  ) : null}
                  {/* De klok bij een overdracht die tijd kost — Sharp's
                      "days later!" (Fig 8.1, p204). De waarde zelf staat als
                      `delay_qualitative` op de stroom («Handoff»); hier staat
                      alleen dat er tijd overheen gaat, met de maat in de
                      tooltip. */}
                  {vertraging ? (
                    <g
                      className="pointer-events-none"
                      transform={`translate(${labelPunt.x + (labelTekst ? 26 : 0)}, ${labelPunt.y})`}
                    >
                      <title>{t("swimlane.delay", { value: vertraging })}</title>
                      <circle r="7" fill="#FFFFFF" stroke={kleur} strokeWidth="1.4" />
                      <path
                        d="M 0 -4 L 0 0 L 3 2"
                        fill="none"
                        stroke={kleur}
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </g>
                  ) : null}
                  {gekozen && ve?.routing === "manual" && !readOnly ? (
                    /* Het stuurpunt verschijnt pas bij selectie — anders staat
                       het canvas vol met grepen die niemand vroeg. */
                    // biome-ignore lint/a11y/noStaticElementInteractions: sleepgreep op een SVG-canvas
                    <circle
                      cx={stuur.x}
                      cy={stuur.y}
                      r={5}
                      fill="#2563EB"
                      className="pointer-events-auto cursor-grab"
                      onPointerDown={(e) => sleepKnik(e, relation.id, start, eind)}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (workspaceId && branchId) {
                          useWorkspaceData
                            .getState()
                            .setSwimlaneKnikOnCanvas(workspaceId, branchId, relation.id, null);
                        }
                      }}
                    />
                  ) : null}
                </g>
              );
            })}
          </svg>

          {/* Het kader om een stap die in meer dan één baan ligt: één stap,
              twee uitvoerders (§11a punt 6). Het vangt geen muis — de kaarten
              eronder blijven gewoon aanwijsbaar. */}
          {model.kaders.map((kader) => (
            <div
              key={`kader-${kader.conceptId}`}
              className="pointer-events-none absolute rounded-lg border-2 border-amber-500/70 border-dashed"
              style={{
                left: kader.x,
                top: kader.y,
                width: kader.breedte,
                height: kader.hoogte,
              }}
            />
          ))}

          {/* stappen */}
          {[...model.kaarten.values()].map((kaart) => {
            const stap = kaart.concept;
            // Breekt deze stap regel A (meer dan één ingang, of meer dan één
            // uitgang zonder beslisstap te zijn)? Bestaande diagrammen worden
            // niet omgebouwd — dat is een keuze van de modelleur — maar de
            // stap krijgt een teken, zodat 'ie te vinden is.
            const regel = isStap(stap)
              ? stroomRegel(
                  stap,
                  model.stromen.map((st) => st.relation),
                  xorIds,
                )
              : null;
            const breektRegel =
              !!regel && (regel.teVeelIn || regel.teVeelUit || regel.moetBeslissen);
            const gekozen = gekozenSleutels.includes(kaart.sleutel);
            const vorm = vormVan(stap);
            const kaartStijl = opmaakVan(kaart.sleutel);
            const levend = sleept?.sleutel === kaart.sleutel ? sleept : null;
            const metend = meet?.sleutel === kaart.sleutel ? meet : null;
            const x = levend ? levend.x : kaart.x;
            const y = levend ? levend.y : kaart.y;
            const breedte = metend ? metend.breedte : kaart.breedte;
            const hoogte = metend ? metend.hoogte : kaart.hoogte;
            // Tijdens het hernoemen komt het invoerveld in de plaats van de
            // kaart: die kaart is zelf een knop, en een veld in een knop is
            // geen geldige opmaak.
            if (hernoemt === kaart.sleutel) {
              return (
                <div
                  key={kaart.sleutel}
                  className="absolute flex flex-col justify-center rounded-md border border-blue-500 bg-white px-2 shadow-sm"
                  style={{ left: x, top: y, width: breedte, height: hoogte }}
                >
                  {naamVeld(
                    kaart.sleutel,
                    stap.id,
                    "w-full rounded border border-blue-400 bg-white px-1 py-0.5 font-medium text-gray-900 text-sm focus:outline-none",
                  )}
                </div>
              );
            }
            return (
              <button
                type="button"
                key={kaart.sleutel}
                data-stap-id={kaart.sleutel}
                onPointerDown={(e) => sleepKaart(e, kaart.sleutel)}
                onClick={(e) => {
                  e.stopPropagation();
                  kiesKaart(kaart.sleutel, stap.id, e);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  kiesKaart(kaart.sleutel, stap.id, e);
                  useUi.getState().setAttributePanelOpen(true);
                }}
                onContextMenu={(e) => openObjectMenu(e, stap.id, kaart.sleutel)}
                className={`absolute flex transition-shadow ${
                  vorm === "vertakking" ? "" : "px-3"
                } ${
                  vorm === "kaart"
                    ? "flex-col justify-center rounded-md border shadow-sm hover:shadow"
                    : vorm === "resultaat"
                      ? "flex-row items-center gap-2"
                      : "flex-col items-center justify-center gap-1"
                } ${gekozen ? "ring-2 ring-blue-300" : ""}`}
                style={{
                  left: x,
                  top: y,
                  width: breedte,
                  height: hoogte,
                  ...(vorm === "kaart"
                    ? {
                        backgroundColor: kaartStijl.fillColor ?? vullingVan(stap),
                        borderColor: kaartStijl.strokeColor ?? (gekozen ? INKT_GEKOZEN : INKT),
                      }
                    : { backgroundColor: "transparent" }),
                  ...(vorm === "kaart" ? {} : { borderWidth: 0 }),
                  ...(vorm === "kaart"
                    ? {}
                    : {
                        backgroundColor: kaartStijl.fillColor ?? "transparent",
                      }),
                  ...(kaartStijl.borderStyle ? { borderStyle: kaartStijl.borderStyle } : {}),
                  ...(kaartStijl.borderRadiusOverride
                    ? { borderRadius: kaartStijl.borderRadiusOverride }
                    : {}),
                  ...(levend ? { opacity: 0.85, cursor: "grabbing" } : { cursor: "grab" }),
                }}
              >
                {vorm === "vertakking" ? null : (
                  <span
                    // De kaartbreedte ligt vast, dus een lange stapnaam paste
                    // niet en werd afgekapt — precies de namen die Sharp
                    // voorschrijft (werkwoord-zelfstandig naamwoord) zijn lang.
                    // Twee regels met een passende tekengrootte, net als op het
                    // gewone canvas; wat dan nog niet past krijgt een tooltip.
                    className="overflow-hidden font-medium text-gray-900 leading-tight"
                    lang={lang}
                    style={{
                      // Hoeveel regels past de kaart? Zoveel mag de naam er
                      // gebruiken. Eerst terugloop, pas daarna kleinere tekst —
                      // een hogere kaart hoort meer tekst te tonen, niet
                      // dezelfde tekst kleiner.
                      // De tekengrootte is een keuze van de modelleur, geen
                      // afleiding: past de naam niet, dan loopt 'ie terug over
                      // meer regels en wordt 'ie desnoods afgekapt (de tooltip
                      // toont 'm heel). Kleiner maken doe je zelf, met de
                      // werkbalk.
                      ...tekstStijl(kaartStijl, DIAGRAM_BASE_FONT),
                      display: "-webkit-box",
                      WebkitLineClamp: regelsInKaart(hoogte, stap),
                      WebkitBoxOrient: "vertical",
                      // De naam bij een stopwatch hangt ONDER het vak. Zat 'ie
                      // erin, dan drukt 'ie het symbool omhoog en ligt het niet
                      // meer op het midden waar de stromen aanhechten — en dan
                      // klopt geen enkele lijn meer. Sharp zet 'm daar ook:
                      // symbool op de lijn, naam eronder.
                      ...(vorm === "wachttijd"
                        ? {
                            position: "absolute" as const,
                            top: "100%",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: KAART_BREEDTE,
                            marginTop: 2,
                            textAlign: (kaartStijl.labelTextAlign ?? "center") as "center",
                          }
                        : {}),
                    }}
                    title={naam(stap)}
                    onDoubleClick={(e) => {
                      // Dubbelklik op de naam hernoemt; op de rest van de kaart
                      // opent 'ie het attributenpaneel.
                      e.stopPropagation();
                      beginHernoemen(kaart.sleutel, stap.id, kaartNaam(stap));
                    }}
                  >
                    {kaartNaam(stap)}
                  </span>
                )}
                {vorm === "vertakking" ? (
                  // Sharp's vertakkingen (H8): een kruis voor "precies één
                  // pad", een neerwaartse punt voor "alle paden", een
                  // opwaartse voor "één of meer". Het symbool staat alleen —
                  // een junction heet niets, en z'n naam op het canvas zetten
                  // maakt de tekening druk zonder iets toe te voegen.
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 26 26"
                    aria-hidden="true"
                    style={{ order: -1, flexShrink: 0 }}
                  >
                    <title>{t(`swimlane.branch.${vertakkingSoort(stap) ?? "xor"}`)}</title>
                    {/* De ring hoort bij een vertakking: daarin staat het teken
                        dat zegt wélke wegen genomen worden. Een samenvoeging
                        heeft dat teken niet en is dus alleen de stip. */}
                    {vertakkingSoort(stap) === "samen" ? null : (
                      <circle
                        cx="13"
                        cy="13"
                        r="11"
                        fill={kaartStijl.fillColor ?? "#FFFFFF"}
                        stroke={kaartStijl.strokeColor ?? INKT}
                        strokeWidth="1.6"
                      />
                    )}
                    {vertakkingSoort(stap) === "samen" ? (
                      // De samenvoeging: een gevulde stip op de lijn. Geen
                      // teken erin, want er valt hier niets te kiezen — de
                      // wegen komen bij elkaar en er gaat er één verder.
                      <circle cx="13" cy="13" r="7" fill={kaartStijl.strokeColor ?? INKT} />
                    ) : vertakkingSoort(stap) === "xor" ? (
                      <path
                        d="M 7 7 L 19 19 M 19 7 L 7 19"
                        stroke={kaartStijl.strokeColor ?? INKT}
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    ) : vertakkingSoort(stap) === "and" ? (
                      <path
                        d="M 7 9 L 13 19 L 19 9"
                        fill="none"
                        stroke={kaartStijl.strokeColor ?? INKT}
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ) : (
                      <path
                        d="M 7 17 L 13 7 L 19 17"
                        fill="none"
                        stroke={kaartStijl.strokeColor ?? INKT}
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                ) : null}
                {vorm === "resultaat" ? (
                  // Sharp's procesresultaat: een cirkel met een zwarte stip.
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 26 26"
                    aria-hidden="true"
                    // Het symbool staat vóór de naam: links van het label bij
                    // een resultaat, erboven bij een wachttijd.
                    style={{ order: -1, flexShrink: 0 }}
                  >
                    <title>{t("swimlane.resultSymbol")}</title>
                    <circle
                      cx="13"
                      cy="13"
                      r="11"
                      fill={kaartStijl.fillColor ?? symboolVulling()}
                      stroke={kaartStijl.strokeColor ?? INKT}
                      strokeWidth="1.6"
                    />
                    <circle cx="13" cy="13" r="5.5" fill={kaartStijl.strokeColor ?? INKT} />
                  </svg>
                ) : null}
                {vorm === "wachttijd" ? (
                  // De stopwatch: deze stap houdt het werk op.
                  <svg
                    width="26"
                    height="28"
                    viewBox="0 0 26 28"
                    aria-hidden="true"
                    style={{ order: -1, flexShrink: 0 }}
                  >
                    <title>{t("swimlane.stepDelays")}</title>
                    <line x1="9" y1="2" x2="17" y2="2" stroke={INKT} strokeWidth="2" />
                    <line x1="13" y1="2" x2="13" y2="6" stroke={INKT} strokeWidth="2" />
                    <circle
                      cx="13"
                      cy="16"
                      r="10"
                      fill={kaartStijl.fillColor ?? symboolVulling()}
                      stroke={INKT}
                      strokeWidth="1.6"
                    />
                    <path
                      d="M 13 10 L 13 16 L 17 19"
                      fill="none"
                      stroke={INKT}
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : null}
                {breektRegel ? (
                  <span
                    className="pointer-events-auto absolute -top-2 -left-2 flex h-4 w-4 items-center justify-center rounded-full border border-amber-600 bg-amber-100 font-bold text-[10px] text-amber-700"
                    title={
                      regel?.teVeelIn || regel?.teVeelUit
                        ? t("swimlane.flowRule.violation")
                        : t("swimlane.flowRule.shouldDecide")
                    }
                    data-stroomregel=""
                  >
                    !
                  </span>
                ) : null}
                {vorm === "kaart" && stap.attributes?.step_contribution === "delays" ? (
                  // Sharp's klok: deze stap houdt het werk op. Op de stroom
                  // zegt 'ie dat de overdracht tijd kost, hier dat de stap
                  // zelf vertraagt (`step_contribution: delays`).
                  <span
                    className="pointer-events-none absolute top-1 right-1 text-[11px] text-gray-500"
                    title={t("swimlane.stepDelays")}
                    aria-hidden="true"
                  >
                    &#9201;
                  </span>
                ) : null}
                {vorm !== "vertakking" && ondertitel(stap) ? (
                  <span
                    className="truncate text-[11px] text-gray-600"
                    style={{ textAlign: kaartStijl.labelTextAlign ?? "center" }}
                  >
                    {ondertitel(stap)}
                  </span>
                ) : null}
              </button>
            );
          })}

          {/* Het rondje waarmee je een stroom trekt, en de greep voor de maat.
              Allebei op de rand van de aangewezen kaart — dezelfde plekken als
              op het gewone canvas. */}
          {(() => {
            const enige = gekozenSleutels.length === 1 ? gekozenSleutels[0] : null;
            const gekozen = enige ? model.kaarten.get(enige) : null;
            if (!gekozen || readOnly) return null;
            const metend = meet?.sleutel === gekozen.sleutel ? meet : null;
            const kaart = {
              ...gekozen,
              breedte: metend ? metend.breedte : gekozen.breedte,
              hoogte: metend ? metend.hoogte : gekozen.hoogte,
            };
            return (
              <>
                <button
                  type="button"
                  key={`stroom-${kaart.sleutel}`}
                  title={t("swimlane.connectHint")}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => sleepStroom(e, kaart.sleutel)}
                  className="absolute h-3 w-3 cursor-crosshair rounded-full border border-blue-500 bg-white"
                  style={{
                    left: kaart.x + kaart.breedte - 6,
                    top: kaart.y + kaart.hoogte / 2 - 6,
                  }}
                />
                <button
                  type="button"
                  key={`maat-${kaart.sleutel}`}
                  title={t("swimlane.resizeHint")}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) =>
                    sleepMaat(e, kaart.sleutel, { breedte: kaart.breedte, hoogte: kaart.hoogte })
                  }
                  className="absolute h-3 w-3 cursor-nwse-resize rounded-full border border-blue-500 bg-white"
                  style={{ left: kaart.x + kaart.breedte - 6, top: kaart.y + kaart.hoogte - 6 }}
                />
              </>
            );
          })()}
          {kader ? (
            <div
              className="pointer-events-none absolute border border-blue-500 bg-blue-400/10"
              style={{
                left: Math.min(kader.x1, kader.x2),
                top: Math.min(kader.y1, kader.y2),
                width: Math.abs(kader.x2 - kader.x1),
                height: Math.abs(kader.y2 - kader.y1),
              }}
            />
          ) : null}
        </div>
      </ZoomPan>

      {vertakkingVraag ? (
        <VertakkingKeuze
          vraag={{ ...vertakkingVraag, ...menuPlek(vertakkingVraag) }}
          onClose={() => setVertakkingVraag(null)}
          onKies={(soort) => {
            const vraag = vertakkingVraag;
            setVertakkingVraag(null);
            void voegVertakkingIn(vraag, soort);
          }}
        />
      ) : null}

      {canvasMenu ? (
        <CanvasContextMenu
          stand={canvasMenu}
          onClose={() => setCanvasMenu(null)}
          onAllesSelecteren={() => setSelectie([...model.kaarten.keys()])}
          onInzoomen={() => zoomActies.current?.inzoomen()}
          onUitzoomen={() => zoomActies.current?.uitzoomen()}
          onTerug={() => zoomActies.current?.terug()}
          onPassend={() => zoomActies.current?.passend()}
        />
      ) : null}
    </div>
  );
}
